import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createVoiceDevice, isNative as isNativePlatform } from './lib/voiceClient';
import { isNativeVoiceEnabled, NativeVoice } from './lib/nativeVoiceClient';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import BiometricSecure from './plugins/biometricSecure';
import BiometricUnlock from './components/BiometricUnlock';
import Login from './components/Login';
import Dialpad from './components/Dialpad';
import ContactPicker from './components/ContactPicker';
import CallControls from './components/CallControls';
import { TabIcons, GearIcon } from './components/TabIcons';
import ParticipantRoster from './components/ParticipantRoster';
import IncomingCall from './components/IncomingCall';
import Voicemail from './components/Voicemail';
import Messages from './components/Messages';
import Contacts from './components/Contacts';
import CallLog from './components/CallLog';
import Settings from './components/Settings';
import ErrorBoundary from './components/ErrorBoundary';
import BuildStamp from './components/BuildStamp';
import TranslateControls from './components/TranslateControls';
import TranslateRecallPrompt from './components/TranslateRecallPrompt';
import { useLineContacts, savedContactName } from './lib/lineContacts';
import { buildTranslateRecall, shouldSuppressRecall } from './lib/translateRecall';
import LiveTranslate from './translate/LiveTranslate';
import API_BASE from './config';
import { clearListCache } from './lib/listCache';

/* Token + app CSS ordering: tokens first (defines vars), App.css uses vars, glass utilities imported after so their classes can override when present */
import './styles/tokens.css';
import './App.css';
import './styles/glass.css';
import './styles/utilities.css';

import AuthProvider from './AuthContext';
import refreshDeviceToken from './lib/refreshDeviceToken';
import {
  readDialDestination,
  validateUsDialNumber,
  UNREACHABLE_MESSAGE,
} from './lib/dialNumber';
import {
  formatCallerIdOption,
  permittedIncomingLineLabel,
} from './lib/permittedIncomingLineLabel';

// Internal legs we dial to ring a device (native SIP find-me ring, the
// conference join leg) use the business DID as their own SIP From — by the
// time that reaches the incoming-call UI it can already be a full SIP URI
// like "sip:+15205551234@some.sip.domain" instead of a plain number. Strip
// the wrapper so the caller-facing UI never shows raw SIP syntax.
function sanitizeCallerDisplay(raw) {
  const s = String(raw || '').trim();
  if (!/^sip:/i.test(s)) return s || 'Unknown';
  const withoutScheme = s.slice(4);
  const at = withoutScheme.indexOf('@');
  const user = at === -1 ? withoutScheme : withoutScheme.slice(0, at);
  return user || 'Unknown';
}

// Saved-contact name for an incoming caller, or null. Contacts are scoped to a
// line, so look in the called line's contacts first, then the selected line's.
function incomingCallerName(number, calledLineContacts, selectedLineContacts) {
  if (String(number || '').replace(/\D/g, '').length < 7) return null;
  return savedContactName(calledLineContacts, number)
    || savedContactName(selectedLineContacts, number)
    || null;
}

/** Per-user Line preference key — never share across SSO users on one device. */
function selectedLineStorageKey(user) {
  const id = String(
    user?.username || user?.email || user?.id || user?.sub || '',
  ).trim().toLowerCase();
  if (!id) return null;
  return `truva.selectedNumber.user.${id}`;
}

function loadSelectedLineForUser(user) {
  const key = selectedLineStorageKey(user);
  if (!key) return '';
  try {
    return localStorage.getItem(key) || '';
  } catch (_) {
    return '';
  }
}

function saveSelectedLineForUser(user, e164) {
  const key = selectedLineStorageKey(user);
  if (!key) return;
  try {
    if (e164) localStorage.setItem(key, e164);
    else localStorage.removeItem(key);
    // Drop legacy org/device-wide key so it cannot override another user.
    localStorage.removeItem('truva.selectedNumber');
  } catch (_) { /* ignore */ }
}

// ── Native console suppression ──
// On Capacitor (iOS/Android), the WebView's console.log output is
// piped to the device syslog and (depending on the Crashlytics setup)
// can chew through quota and battery. Replace the chatty info/log/debug
// methods with no-ops at module load so they're suppressed before any
// component code runs. WARN and ERROR are preserved so genuine problems
// still surface in adb logcat / Xcode console / crash reports.
if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
  const noop = () => {};
  /* eslint-disable no-console */
  console.log = noop;
  console.info = noop;
  console.debug = noop;
  console.trace = noop;
  /* eslint-enable no-console */
}

// Native audio route plugin (iOS + Android). Owns earpiece/speaker routing
// now that call media runs in the WebView via @signalwire/js. Imported from
// the shared module rather than calling registerPlugin('AudioRoute') again
// here — doing both made Capacitor log "plugin already registered" on every
// launch, and the module also supplies no-op web fallbacks.
import AudioRoute from './plugins/audioRoute';
import { callKitSafe } from './plugins/callKit';

const TABS = ['phone', 'voicemail', 'recents', 'contacts', 'messages'];
/** Tabs always visible on a narrow phone; the rest go under ⋯ More. */
const NARROW_PRIMARY_TABS = ['phone', 'messages', 'recents', 'contacts'];
const NARROW_MORE_TABS = TABS.filter((t) => !NARROW_PRIMARY_TABS.includes(t));
/** Phones in portrait (and short Android WebViews) need overflow menus. */
const NARROW_LAYOUT_MQ = '(max-width: 430px)';

const TAB_LABELS = {
  phone: 'Keypad',
  voicemail: 'Voicemail',
  recents: 'Recents',
  contacts: 'Contacts',
  messages: 'Messages',
  settings: 'Settings',
};

const TAB_ICONS = {
  phone: TabIcons.phone,
  voicemail: TabIcons.voicemail,
  recents: TabIcons.recents,
  contacts: TabIcons.contacts,
  messages: TabIcons.messages,
  settings: TabIcons.settings,
};

// ── Audio Alerts ────────────────────────────────────────
let audioCtx = null;

// Some WKWebView versions only honor AudioContext.resume() when it's
// called synchronously inside a real user-gesture event handler — calling
// it later from an incoming-call callback (never a gesture) may not be
// enough to unlock a context that has never played anything yet. Belt and
// suspenders: unlock on the very first tap anywhere in the app, so by the
// time a call actually arrives the context is already running and the
// resume() call in playRingtone() is a no-op safety net, not the only
// thing standing between an incoming call and total silence.
if (typeof document !== 'undefined') {
  const unlockAudioOnce = () => {
    try {
      const ctx = getAudioCtx();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch (_) {}
    document.removeEventListener('touchstart', unlockAudioOnce);
    document.removeEventListener('click', unlockAudioOnce);
  };
  document.addEventListener('touchstart', unlockAudioOnce, { once: true, passive: true });
  document.addEventListener('click', unlockAudioOnce, { once: true });
}
function getAudioCtx() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtx;
}

// Play a repeating ringtone pattern (call)
function playRingtone() {
  const ctx = getAudioCtx();
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.type = 'sine';
  oscillator.frequency.value = 440;
  gain.gain.value = 0.3;

  const schedule = () => {
    // Read currentTime only once actually running — it's frozen while
    // suspended, so scheduling against it before resume() resolves would
    // misalign the pattern against the real clock once playback starts.
    const now = ctx.currentTime;
    // Ring pattern: 1s on, 2s off, repeat
    for (let i = 0; i < 10; i++) {
      gain.gain.setValueAtTime(0.3, now + i * 3);
      oscillator.frequency.setValueAtTime(440, now + i * 3);
      oscillator.frequency.setValueAtTime(480, now + i * 3 + 0.5);
      gain.gain.setValueAtTime(0, now + i * 3 + 1);
    }
    oscillator.start(now);
    oscillator.stop(now + 30);
  };

  // WKWebView (like Safari) suspends a fresh/idle AudioContext until a
  // user gesture unlocks it. An incoming call arrives from the network,
  // never from a gesture, so without this the oscillator schedules with
  // no error but is never audible. Confirmed on device: the incoming-call
  // UI showed for several seconds with zero sound, and this reproduced
  // even after fixing the native AVAudioSession side (a separate, real
  // fix, but not the actual cause of the missing ringtone — this is a
  // WebView-side policy, independent of anything native).
  if (ctx.state === 'suspended') {
    ctx.resume().then(schedule).catch((e) => console.warn('[Ringtone] resume failed:', e));
  } else {
    schedule();
  }

  return { oscillator, gain, ctx };
}

// Play a short chime (new message)
function playMessageChime() {
  const ctx = getAudioCtx();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'sine';
  const now = ctx.currentTime;
  osc.frequency.setValueAtTime(880, now);
  osc.frequency.setValueAtTime(1100, now + 0.1);
  osc.frequency.setValueAtTime(880, now + 0.2);
  gain.gain.setValueAtTime(0.25, now);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
  osc.start(now);
  osc.stop(now + 0.4);
}

function stopRingtone(ref) {
  if (ref?.oscillator) {
    try { ref.oscillator.stop(); } catch (e) { /* already stopped */ }
  }
}

/** Pre-warm Chromium's WebRTC/P2P networking service by opening a
 *  throwaway RTCPeerConnection pointed at SignalWire's TURN host as soon
 *  as a ring is detected, well before Accept.
 *
 *  Root cause (found via live device logs + logcat during an Android
 *  test call): the WebView's P2P socket manager returns
 *  net::ERR_NAME_NOT_RESOLVED for turn.signalwire.com specifically in the
 *  first few seconds after the app resumes from backgrounded — a
 *  Chromium-internal timing issue, not a real DNS/network problem (a
 *  plain `ping turn.signalwire.com` from the same device resolves and
 *  responds fine). With no retry, connectInboundJoin's ICE gathering
 *  just times out after 20s, so the agent-side UI shows "Connected"
 *  (the answer-inbound REST call succeeded independently) while no
 *  media ever actually flows — the real caller hears nothing and hangs
 *  up. This doesn't need real TURN credentials: ICE candidate gathering
 *  attempts DNS resolution before any auth handshake, so a throwaway,
 *  unauthenticated connection exercises and warms the exact subsystem
 *  that failed.
 */
let _iceNetworkingWarmed = false;
function warmIceNetworking() {
  if (_iceNetworkingWarmed || typeof RTCPeerConnection === 'undefined') return;
  _iceNetworkingWarmed = true;
  try {
    const pc = new RTCPeerConnection({
      // A turn: URL must carry credentials or the constructor throws and nothing is warmed. Throwaway
      // values are fine: gathering resolves DNS before any auth handshake.
      iceServers: [
        { urls: 'stun:turn.signalwire.com:3478' },
        { urls: 'turn:turn.signalwire.com:3478', username: 'warm', credential: 'warm' },
      ],
    });
    pc.createDataChannel('truva-ice-warm');
    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer))
      .catch((e) => console.warn('[INCOMING] ICE warm-up offer failed:', e?.message || e));
    setTimeout(() => {
      try { pc.close(); } catch (_) {}
    }, 8000);
  } catch (e) {
    console.warn('[INCOMING] ICE warm-up failed:', e?.message || e);
  }
}

/** Release ring-time getUserMedia so the softphone mic does not keep
 *  capturing room audio (TV) after the inbound UI is dismissed. */
function releaseWarmMic() {
  try {
    const warm = typeof window !== 'undefined' ? window.__truvaWarmMic : null;
    if (!warm) return;
    warm.getTracks?.().forEach((t) => {
      try { t.stop(); } catch (_) {}
    });
    window.__truvaWarmMic = null;
  } catch (_) { /* ignore */ }
}

function vibrate(pattern) {
  if (navigator.vibrate) navigator.vibrate(pattern);
}

function sendNotification(title, body) {
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    new Notification(title, { body, icon: '📱' });
  }
}

function App() {
  if (typeof window !== 'undefined' && typeof window.__truvaWakeLockActive === 'undefined') {
    window.__truvaWakeLockActive = false;
  }
  // ── Auth State ─────────────────────────────────────────
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('authToken') || '');
  // Biometric re-auth gate: on cold launch to the foreground, if a
  // biometric-protected session token exists on-device, show an unlock screen
  // instead of the main UI until the OS releases the token after a live
  // Face ID / fingerprint check. Plaintext localStorage stays the always-on
  // token (background/native refresh can't prompt); this gate adds a
  // 'did a human unlock this process' step on cold start.
  //
  // Tri-state, not boolean: `null` means "haven't asked the native side yet"
  // and must render neither the real app nor the unlock screen — hasToken()
  // is an async native-bridge round trip, so a boolean starting at `false`
  // would let the very first render paint the real authenticated app for a
  // beat before the effect below ever resolves, defeating the whole point of
  // the gate. Only a native platform with an existing plaintext token even
  // has something to check; anything else starts settled at `false`.
  const [bioGate, setBioGate] = useState(() => (
    Capacitor.isNativePlatform() && localStorage.getItem('authToken') ? null : false
  ));
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  }), [authToken]);

  // Refresh role + live Hub plan. Tenant plan can change after login
  // (upgrade, ops grant); poll so Recents/transcripts flip without sign-out.
  useEffect(() => {
    if (!authToken) return undefined;
    let cancelled = false;
    const applyMe = (me) => {
      if (cancelled || !me) return;
      setUser((prev) => {
        const next = { ...(prev || {}), ...me, role: me.role || 'agent' };
        const same = prev
          && prev.role === next.role
          && prev.plan === next.plan
          && JSON.stringify(prev.entitlements || {}) === JSON.stringify(next.entitlements || {})
          && JSON.stringify(prev.limits || {}) === JSON.stringify(next.limits || {});
        if (same) return prev;
        try { localStorage.setItem('user', JSON.stringify(next)); } catch (_) { /* ignore */ }
        return next;
      });
    };
    const refreshMe = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/me`, {
          headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
        });
        if (!r.ok || cancelled) return;
        applyMe(await r.json());
      } catch (_) { /* ignore */ }
    };
    refreshMe();
    const interval = setInterval(refreshMe, 15000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshMe();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refreshMe);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshMe);
    };
  }, [authToken]);

  // Item 8: generate a fresh Idempotency-Key per user gesture. Use crypto.randomUUID
  // when available (all modern browsers + secure contexts) with a fallback for
  // older environments. The backend caches the response for IDEMPOTENCY_TTL_SECONDS
  // (default 5 minutes) keyed by (key, user, endpoint), so retries within the
  // window get the original response replayed.
  const newIdempotencyKey = useCallback(() => {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch (e) { /* fall through */ }
    return 'idk-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }, []);

  const handleLogout = async () => {
    // Local sign-out must never wait on the network: on iOS a stalled request (app just
    // resumed, weak signal) made the tap look dead. Capture what the server calls need, tear
    // down local state first, and let the server calls finish in the background with a timeout.
    const tokenAtLogout = authToken;
    let pushToken = null;
    try { pushToken = window.localStorage.getItem('truvaRegisteredPushToken'); } catch (_) { /* ignore */ }
    const bestEffortPost = (path, headers, body) => {
      try {
        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = ctrl ? setTimeout(() => ctrl.abort(), 4000) : null;
        fetch(`${API_BASE}${path}`, { method: 'POST', headers, body, signal: ctrl ? ctrl.signal : undefined })
          .catch(() => {})
          .finally(() => { if (timer) clearTimeout(timer); });
      } catch (_) { /* never block logout */ }
    };
    // Drop this device's push token from the account being signed out. Without this the phone
    // kept ringing for the previous account (the token stayed filed under it) after logout.
    if (pushToken && tokenAtLogout) {
      bestEffortPost('/api/push/invalidate-device',
        { Authorization: `Bearer ${tokenAtLogout}`, 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
        JSON.stringify({ device_token: pushToken }));
    }
    try { window.localStorage.removeItem('truvaRegisteredPushToken'); } catch (_) { /* ignore */ }
    bestEffortPost('/api/logout',
      { Authorization: `Bearer ${tokenAtLogout}`, 'ngrok-skip-browser-warning': 'true' });
    // Truva's own session is gone, but Hub's separate SSO cookie isn't —
    // Login's auto-redirect-to-Hub effect (for brand-new visitors) would
    // otherwise immediately bounce through /auth/sso/login and silently
    // re-authenticate off that still-live cookie, with no visible sign of
    // having logged out at all. Flag this as an explicit sign-out so Login
    // shows the real screen instead of auto-bouncing.
    try { sessionStorage.setItem('truva_just_signed_out', '1'); } catch (_) { /* ignore */ }
    setAuthToken('');
    setUser(null);
    setSelectedNumber('');
    setPhoneNumbers([]);
    clearListCache();
    localStorage.removeItem('authToken');
    try { BiometricSecure.clearToken().catch(() => {}); } catch (_) { /* ignore */ } // drop the biometric-gated copy on logout
    localStorage.removeItem('user');
    // Do not clear per-user line prefs — next login for that user restores theirs.
    // Product rule: only logout tears down SIP + stored creds.
    try { deviceRef.current?.destroy?.({ clearCredentials: true }); } catch (_) { /* ignore */ }
    deviceRef.current = null;
    setDeviceReady(false);
  };

  // ── State ──────────────────────────────────────────────
  const [tab, setTab] = useState('phone');
  const [number, setNumber] = useState('');
  // Synchronous dial buffer. React setState is async; if Call is tapped in
  // the same turn as the last digit, `number` can still be missing that
  // keypress (prod: 6575033066 → To=+657503306). handleCall reads this ref.
  const numberRef = useRef('');
  const setDialNumber = (next) => {
    const value = typeof next === 'function' ? next(numberRef.current) : next;
    numberRef.current = value == null ? '' : String(value);
    setNumber(numberRef.current);
  };
  const [status, setStatus] = useState('Ready');
  const [deviceReady, setDeviceReady] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  // Whether a native-SIP (CallKit + Linphone) call is currently ringing or
  // connected. Tracked independently of authToken/user — a VoIP push can
  // answer a call entirely natively while logged out (see CallKitManager.
  // swift's currentCallIsNativeSIP path), and the render gate below must
  // not stomp that in-progress call with the Login screen just because
  // there's no session. Query on mount covers a cold VoIP-push launch
  // where the truva:nativeCallActive window event fired before this
  // component (and its listener) existed.
  const [nativeCallActive, setNativeCallActive] = useState(false);
  // Arm the biometric unlock gate on cold launch, when a secure token exists.
  useEffect(() => {
    let cancelled = false;
    if (
      typeof window !== 'undefined' &&
      Capacitor.isNativePlatform() &&
      localStorage.getItem('authToken')
    ) {
      BiometricSecure.hasToken()
        .then((r) => {
          // Settle the tri-state either way -- leaving it at `null` on a
          // negative/failed check would strand the app on the splash screen
          // forever instead of falling through to the main UI.
          if (!cancelled) setBioGate(!!(r && r.has));
        })
        .catch(() => {
          if (!cancelled) setBioGate(false);
        });
    }
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onNativeCallActive = (ev) => setNativeCallActive(!!ev.detail?.active);
    window.addEventListener('truva:nativeCallActive', onNativeCallActive);
    if (isNativePlatform && NativeVoice?.isNativeCallActive) {
      NativeVoice.isNativeCallActive()
        .then(({ active }) => setNativeCallActive(!!active))
        .catch(() => {});
    }
    return () => window.removeEventListener('truva:nativeCallActive', onNativeCallActive);
  }, []);
  // Gate Hold/Transfer/Add until Twilio fires the `accept` event so the
  // backend has had time to receive participant-join from /conference-status
  // and populate conference_participants. Avoids 409 no_conference_participant
  // and 409 original_participant_not_found from clicking controls during
  // the dialing/ringing window.
  const [callConnected, setCallConnected] = useState(false);
  // Twilio SDK call status: 'closed' | 'connecting' | 'ringing' | 'open'.
  // Driven directly by SDK events so Hold/3-Way buttons only appear once
  // the call is truly connected ('open'), not during dialing/ringing.
  const [callStatus, setCallStatus] = useState('closed');
  // False-disconnect guard: timestamp (ms) of the most recent backend
  // operation that might cause a TRANSIENT SDK `disconnect` event. When
  // the SDK fires `disconnect` within a short window after one of these,
  // the disconnect handler will verify against the backend's view of the
  // call before tearing down the UI.
  const recentBackendOpRef = useRef(0);

  // Handoff grace window: after a successful /call/add, the backend
  // redirects the SDK leg into the conference (~1s end-to-end). During
  // that window the Twilio SDK can fire a transient `disconnect` event
  // AND the backend's /call/status briefly reads `completed` for the
  // old call segment before the new one shows `in-progress`. Without a
  // grace, the frontend treats both as a real hangup and tears down
  // before Participant 3 can even ring.
  //
  // The grace MUST be armed only after the /call/add fetch resolves
  // OK (form-submission moment), NOT when the Add button is clicked.
  // The Add button opens a prompt() which blocks for as long as the
  // user takes to type a number — arming earlier would let the window
  // burn down before the request even leaves the browser.
  //
  // EVENT-DRIVEN HANDOFF LOCK — no fixed timer. The lock is held
  // from OK-click until the first roster change confirms the bridge
  // has transitioned. Two release triggers (see the participants
  // useEffect below):
  //   (a) roster shrinks (original leg terminated) — emits REAL-TIME
  //       LATENCY log with OK-click anchor
  //   (b) roster grows above baseline (new participant joined) —
  //       releases silently
  // Plus user-initiated release via handleHangup → clearHandoffGrace.
  //
  // Refs (not state) so disconnect-handler closures read them
  // synchronously without stale-closure issues:
  //   - handoffActiveRef:  boolean lock — true while in handoff
  //   - handoffArmedAtRef: arm timestamp for "sinceArm" diagnostic logs
  const handoffActiveRef = useRef(false);
  const handoffArmedAtRef = useRef(0);

  // isBridgingRef — semantic alias for handoffActiveRef used by the
  // handleHangup guard. Whenever a bridge transition is in flight (the
  // backend is dialing the third party / waiting for participant-join),
  // handleHangup REFUSES the teardown so a programmatic or stale-handler
  // click can't kill the active leg mid-bridge. Kept as a separate ref
  // (rather than re-reading handoffActiveRef directly) so the intent at
  // the check site reads as "are we bridging?" rather than "is the grace
  // active?".
  const isBridgingRef = useRef(false);

  // handoffClickTimeRef — wall-clock timestamp (ms) captured at the
  // PRECISE moment the user clicks "OK" on the Add-participant prompt,
  // AFTER they have typed the number. The participants-poll useEffect
  // uses this to compute the REAL-TIME LATENCY log when the original
  // leg's termination is observed in the roster.
  const handoffClickTimeRef = useRef(0);

  // baselinePCountRef — roster size at OK-click time. The participants
  // useEffect compares this to the polled count to detect:
  //   • count DECREASE → original leg terminated → release + latency log
  //   • count INCREASE → new participant joined → release (no latency log)
  // The lock is purely event-driven (no setTimeout fallback) — one of
  // these two changes is expected to fire within a few seconds.
  const baselinePCountRef = useRef(0);

  // isBridgingState — React-state mirror of isBridgingRef so the UI
  // (specifically the End button in CallControls) can re-render when
  // the lock arms/releases. Refs alone do not trigger re-renders.
  const [isBridgingState, setIsBridgingState] = useState(false);

  // armHandoffGrace — call from the OK-click moment in handleAddCall
  // (post-prompt, pre-fetch) to lock the UI against teardown until the
  // bridge transition is event-confirmed. Idempotent: re-arming just
  // refreshes the click-time anchor and baseline roster size.
  const armHandoffGrace = () => {
    // No setTimeout — purely event-driven release. The participants
    // useEffect below releases the lock when EITHER:
    //   (a) the original leg's termination is observed (roster shrinks)
    //   (b) the new participant joins (roster grows above baseline)
    // Whichever happens first releases — the bridge is then known to
    // be in a stable state and the UI can resume normal disconnect
    // handling. No fixed timeout, no false narrative.
    handoffActiveRef.current = true;
    isBridgingRef.current = true;
    setIsBridgingState(true);
    handoffArmedAtRef.current = Date.now();
    baselinePCountRef.current = participants.length;
    console.log(
      `[CALL ADD] HANDOFF GRACE armed (event-driven release; baseline_participants=${baselinePCountRef.current}) ` +
      `— will release on first roster change (original leg termination OR new participant join)`
    );
  };

  // clearHandoffGrace — callers:
  //   • the participants useEffect (event-driven release: original leg
  //     termination OR new participant join)
  //   • handleHangup (user explicitly clicks End; only fires if the
  //     bridge guard hasn't already blocked the click)
  const clearHandoffGrace = (reason) => {
    if (handoffActiveRef.current) {
      handoffActiveRef.current = false;
      isBridgingRef.current = false;
      setIsBridgingState(false);
      handoffClickTimeRef.current = 0;
      baselinePCountRef.current = 0;
      console.log(`[CALL ADD] HANDOFF GRACE cleared (reason=${reason}, isBridging=false)`);
    }
  };

  // Maximum time (ms) after a backend op during which we'll bother
  // verifying with Twilio. Outside this window, a `disconnect` event
  // is almost certainly a normal hangup — skipping the HTTP probe
  // saves a round-trip and tears down the UI immediately.
  //
  // 60s gives 3-way calling plenty of breathing room — Twilio conference
  // state changes, third-party dial-out, and per-leg status-callback
  // round-trips can take 30-40s observed in practice. 60s covers that
  // comfortably with margin.
  const GUARD_WINDOW_MS = 60000;

  // verifyCallStillAlive — verify against Twilio's authoritative call
  // status before tearing down on a SDK `disconnect` event. Inside the
  // guard window we always probe; outside it we fast-path to "real
  // disconnect" without an HTTP call.
  // probeCallAlive — unconditional authoritative check against the backend.
  // Unlike verifyCallStillAlive it applies no guard-window heuristics: it
  // always performs the HTTP probe and answers purely on the carrier's call
  // status. Used by the conference-join-grace path, where a stale
  // recentBackendOp must NOT be allowed to short-circuit to "dead" and tear
  // down a call that is merely mid-join. Returns true/false, or null when the
  // probe itself is inconclusive so callers can leave the call alone.
  const probeCallAlive = useCallback(async (callSid) => {
    if (!callSid) return null;
    try {
      const r = await fetch(
        `${API_BASE}/call/status?call_sid=${encodeURIComponent(callSid)}`,
        { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } }
      );
      if (!r.ok) return null;
      const data = await r.json();
      const status = (data && data.status) || '';
      if (!status) return null;
      const alive = ['in-progress', 'ringing', 'answered', 'active', 'queued', 'initiated'].includes(status);
      console.log(`[CALL] probeCallAlive(${callSid}) backend_status=${status} → ${alive}`);
      return alive;
    } catch (e) {
      return null;
    }
  }, [authToken]);

  const verifyCallStillAlive = useCallback(async (callSid) => {
    // HANDOFF LOCK SHORT-CIRCUIT — when the lock is active, the bridge
    // is mid-transition and ANY backend_status (completed, failed,
    // even an HTTP error on /call/status) must NOT trigger teardown.
    // The lock holds for the FULL 30s, released only by setTimeout
    // expiry or explicit user-hangup. Participant-join no longer
    // releases the lock — see the comment block on HANDOFF_GRACE_MS
    // for the rationale (transient disconnect lands AFTER the join).
    if (handoffActiveRef.current) {
      console.log('[DEBUG] Guarding transient disconnect during bridge transition');
      return true;
    }
    if (!callSid) {
      console.log('[CALL] verifyCallStillAlive: EXIT no-callSid (call.parameters.CallSid was falsy when disconnect fired)');
      return false;
    }
    const opAt = recentBackendOpRef.current;
    const sinceOp = opAt ? (Date.now() - opAt) : 0;
    // !opAt (no backend op has EVER been recorded, e.g. a plain inbound
    // call that never went through Add/Transfer/Hold) must fast-path the
    // same as a STALE op — otherwise this falls through to the backend
    // fetch below unconditionally, for the entire lifetime of the call.
    // /call/status reports the shared orchestrator/conference record, which
    // stays "in-progress" as long as ANY leg (e.g. the web tab) is still
    // attached, so a genuine native hangup got permanently misread as
    // "still alive" and the disconnect was ignored forever.
    if (!opAt || sinceOp > GUARD_WINDOW_MS) {
      console.log(`[CALL] verifyCallStillAlive(${callSid}): EXIT outside-window sinceBackendOp=${sinceOp}ms threshold=${GUARD_WINDOW_MS}ms (no recent backend op — fast-path teardown)`);
      return false;
    }
    try {
      const r = await fetch(
        `${API_BASE}/call/status?call_sid=${encodeURIComponent(callSid)}`,
        { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } }
      );
      if (!r.ok) {
        console.log(`[CALL] verifyCallStillAlive(${callSid}): EXIT http-${r.status} sinceBackendOp=${sinceOp}ms (backend /call/status not 200)`);
        return false;
      }
      const data = await r.json();
      const status = (data && data.status) || '';
      const stillAlive = ['in-progress', 'ringing', 'answered', 'active', 'queued', 'initiated'].includes(status);
      console.log(
        `[CALL] verifyCallStillAlive(${callSid}) ` +
        `sinceBackendOp=${sinceOp}ms backend_status=${data && data.status} → ${stillAlive}`
      );
      return stillAlive;
    } catch (e) {
      console.warn(`[CALL] verifyCallStillAlive(${callSid}): EXIT fetch-error sinceBackendOp=${sinceOp}ms`, e);
      return false;
    }
  }, [authToken]);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(false);
  const [isBluetooth, setIsBluetooth] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBusy, setRecordingBusy] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  // Shown whenever a call connects; the backend hides it (403/409) if the
  // line mode or plan does not permit recording. Server is the gate.
  const [recordingSupported, setRecordingSupported] = useState(true);
  const [recordingError, setRecordingError] = useState(null);

  const [isHoldPending, setIsHoldPending] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callTimer, setCallTimer] = useState(0);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState('');
  const [narrowLayout, setNarrowLayout] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(NARROW_LAYOUT_MQ).matches
      : true
  ));
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [moreTabsOpen, setMoreTabsOpen] = useState(false);
  const softphoneRef = useRef(null);
  const accountMenuRef = useRef(null);
  const moreTabsRef = useRef(null);

  // Narrow phones clip the header email + rightmost bottom tabs — switch to ⋯ menus.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia ? window.matchMedia(NARROW_LAYOUT_MQ) : null;
    const apply = () => {
      let narrow = mq ? mq.matches : false;
      try {
        const el = softphoneRef.current;
        // Softphone is often 375px even on a wide desktop window.
        if (el && el.clientWidth > 0 && el.clientWidth <= 430) narrow = true;
      } catch (_) { /* ignore */ }
      setNarrowLayout(narrow);
    };
    apply();
    const raf = requestAnimationFrame(apply);
    const onMq = () => apply();
    mq?.addEventListener?.('change', onMq);
    window.addEventListener('resize', apply);
    let ro;
    try {
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(apply);
        if (softphoneRef.current) ro.observe(softphoneRef.current);
        // Softphone mounts after auth; observe on next frame if needed.
        requestAnimationFrame(() => {
          if (softphoneRef.current && ro) {
            try { ro.observe(softphoneRef.current); } catch (_) { /* ignore */ }
          }
          apply();
        });
      }
    } catch (_) { /* ignore */ }
    return () => {
      cancelAnimationFrame(raf);
      mq?.removeEventListener?.('change', onMq);
      window.removeEventListener('resize', apply);
      try { ro?.disconnect(); } catch (_) { /* ignore */ }
    };
  }, [authToken]);

  // Close overflow menus on outside tap / Escape.
  useEffect(() => {
    if (!accountMenuOpen && !moreTabsOpen) return undefined;
    const onPointer = (e) => {
      const t = e.target;
      if (accountMenuOpen && accountMenuRef.current && !accountMenuRef.current.contains(t)) {
        setAccountMenuOpen(false);
      }
      if (moreTabsOpen && moreTabsRef.current && !moreTabsRef.current.contains(t)) {
        setMoreTabsOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setAccountMenuOpen(false);
        setMoreTabsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountMenuOpen, moreTabsOpen]);

  // Dropdown is the source of truth: keep state clamped to an owned DID
  // (or empty). Never leave selectedNumber pointing at a number that is
  // not currently shown in the list. Preference is per logged-in user.
  useEffect(() => {
    if (!phoneNumbers.length) {
      if (selectedNumber) setSelectedNumber('');
      return;
    }
    const owned = phoneNumbers.map((n) => n.phone_number);
    if (selectedNumber && owned.includes(selectedNumber)) return;
    if (owned.length === 1) {
      setSelectedNumber(owned[0]);
      saveSelectedLineForUser(user, owned[0]);
      return;
    }
    const saved = loadSelectedLineForUser(user);
    if (saved && owned.includes(saved)) {
      setSelectedNumber(saved);
      return;
    }
    if (selectedNumber) setSelectedNumber('');
  }, [phoneNumbers, selectedNumber, user]);
  const [composeTo, setComposeTo] = useState(null);
  // A phone number to prefill when opening the New-Contact add form. Set by
  // CallLog/Messages 'Add to Contacts'; Contacts consumes it once then clears it
  // (same lifecycle as composeTo).
  const [prefillContactNumber, setPrefillContactNumber] = useState(null);
      const [tabResetKeys, setTabResetKeys] = useState({ messages: 0, recents: 0, contacts: 0, voicemail: 0 });
      const [unreadMessages, setUnreadMessages] = useState(0);
  const [unheardVoicemails, setUnheardVoicemails] = useState(0);
  // iOS notification (APNs alert) permission: 'denied' means the user said no
  // to the system prompt, so Settings shows a "turn notifications on" hint
  // instead of the app silently getting no alerts. See AlertPushManager.swift.
  const [notificationPermission, setNotificationPermission] = useState('unknown');
  const [participants, setParticipants] = useState([]);

  // Contacts cache for ParticipantRoster name resolution (so "Unknown"
  // gets replaced with the saved contact name when a participant's
  // from/to number matches a contact). Refreshed once on login and
  // every 60s to pick up adds/edits done in the Contacts tab.
  const [contacts, setContacts] = useState([]);

  // Dual-screen toggle for active-call UI. When activeCall is non-null:
  //   showDialpad === false → CallControls (Hold/Add/Transfer/End/etc.)
  //   showDialpad === true  → Dialpad (for DTMF input mid-call)
  // Resets to false on every disconnect/teardown so the next call lands
  // on CallControls by default ("auto-switch on connect").
  const [showDialpad, setShowDialpad] = useState(false);
  // When set, the mid-call Dialpad screen is in number-entry mode for Add
  // or Transfer instead of its default DTMF mode — digits accumulate into
  // dialpadNumber instead of being sent as touch-tones, and the green Call
  // button submits to handleAddCall/handleTransfer. Replaces the old
  // window.prompt() dialogs, which were a third, inconsistent way to type
  // a number distinct from both the main dialpad and each other.
  const [dialpadTarget, setDialpadTarget] = useState(null); // null | 'add' | 'transfer'
  const [dialpadNumber, setDialpadNumber] = useState('');
  // Echo of DTMF digits pressed mid-call (display only — tones still go via sendDigits).
  const [dtmfEcho, setDtmfEcho] = useState('');
  // AI-transfer whisper gate. When the answered inbound call belongs to an
  // AI-transfer conference (conf_name starts with "Xfer_"), the server plays
  // "Press 1 to accept, 2 to decline" and waits ~10s for a DTMF keypress on
  // this leg. While set, the Accept (1)/Decline (2) banner renders with the
  // in-call controls.
  const [transferGate, setTransferGate] = useState(null); // null | { conf }
  const transferGateTimerRef = useRef(null);
  // Conf whose gate was already dismissed, so a late answer-inbound response
  // cannot re-arm the banner after the agent pressed a key.
  const transferGateDismissedRef = useRef('');

  // Live Translate state
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslate, setShowTranslate] = useState(false);
  const [savedContacts, reloadSavedContacts] = useLineContacts(selectedNumber, authToken);
  // Contacts belong to a line, so the caller's name must come from the line
  // being CALLED (which may not be the selected one). Empty when not ringing.
  const incomingLineNumber = incomingCall
    ? String(incomingCall._inboundMeta?.to || incomingCall.parameters?.To || '')
    : '';
  const [incomingLineContacts] = useLineContacts(incomingLineNumber, authToken);
  const [recallPrompt, setRecallPrompt] = useState(null);
  const lastTranslateRef = useRef(null);
  const [showLiveTranslate, setShowLiveTranslate] = useState(false);
  const [liveTranslateActive, setLiveTranslateActive] = useState(false);
  const [liveTranslateInitialAction, setLiveTranslateInitialAction] = useState(null);
  const liveTranslateRef = useRef(null);

  // In-Person Translate lives in Settings (single activation point — no
  // more dialpad globe icon forcing the keypad off-center). Starting it
  // switches to the Phone tab since that's where the session UI (mic,
  // captions, stop) actually renders; stopping can happen from wherever.
  const handleTranslateToggleClick = () => {
    if (showLiveTranslate) {
      liveTranslateRef.current?.toggle();
    } else {
      // Do NOT auto-start here: Live Translate mounts asynchronously, so
      // invoking start() from a mount effect would be outside the user
      // gesture and the AudioContext would get suspended (no audio ever
      // reaches the server). Open the sheet instead; the user taps Start,
      // which creates the AudioContext synchronously inside the click.
      setLiveTranslateInitialAction(null);
      setShowLiveTranslate(true);
      setTab('phone');
    }
  };

  // Device init controls: do NOT init SignalWire Device until user gesture
  // (Connect button / Phone tab). Incrementing connectGeneration forces a
  // full teardown + re-init on Retry Connect (needed because phone-tab
  // selection alone would keep the init effect armed).
  const [deviceInitRequested, setDeviceInitRequested] = useState(false);
  const [deviceConnectGeneration, setDeviceConnectGeneration] = useState(0);
  const [phoneTabSelectedByUser, setPhoneTabSelectedByUser] = useState(false);

  const deviceRef = useRef(null);
  // Hysteresis for the Online badge: a SIP re-REGISTER (foreground resume,
  // Linphone "Cleared" housekeeping, network blip) briefly reports
  // unregistered and then Ok a second or two later. Flipping the badge to
  // Connecting/Retry Connect for that window made the line look unstable.
  // Only show offline if it is still unregistered after the grace period.
  const offlineTimerRef = useRef(null);
  const OFFLINE_GRACE_MS = 8000;
  const markOnline = () => {
    clearTimeout(offlineTimerRef.current);
    offlineTimerRef.current = null;
    setDeviceReady(true);
    setStatus('Ready');
  };
  const markOfflineSoon = () => {
    if (offlineTimerRef.current) return;
    offlineTimerRef.current = setTimeout(() => {
      offlineTimerRef.current = null;
      setDeviceReady(false);
      if (!callConnectedRef.current) setStatus('Phone line reconnecting…');
    }, OFFLINE_GRACE_MS);
  };
  const timerRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callKitUuidRef = useRef(null);
  const wakeLockRef = useRef(null);
  const handleAcceptRef = useRef(null);
  // Last native wake handled (call sid + autoAccept), so the same call is not processed twice.
  const lastWakeKeyRef = useRef('');
  const handleRejectRef = useRef(null);
  const incomingCallRef = useRef(null);
  const activeCallRef = useRef(null);
  // Suppress pending-inbound re-entry while reject/hangup clears the ring.
  const inboundDismissingRef = useRef(false);
  // Mirrors deviceInitRequested||phoneTabSelectedByUser, always fresh (see
  // the device-init effect's cleanup below for why this exists).
  const deviceWantedRef = useRef(false);
  deviceWantedRef.current = deviceInitRequested || phoneTabSelectedByUser;
  const inboundExpireTimerRef = useRef(null);
  // Timestamp of the most recent user-initiated hangup. Used to suppress
  // Twilio error 31005 (ConnectionError) which fires at hangup as a
  // side-effect of WebRTC teardown — not a genuine call failure.
  const hangupInitiatedAtRef = useRef(0);
  const callConnectedRef = useRef(false);
  // session sentinel worker ref
  const sentinelRef = useRef(null);
  const keepaliveIntervalRef = useRef(null);

  const requestWakeLock = useCallback(async () => {
    try {
      if (typeof navigator !== 'undefined' && 'wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          wakeLockRef.current.addEventListener('release', () => {
            console.log('[WAKE] released');
            try { window.__truvaWakeLockActive = false; } catch (e) {}
          });
          window.__truvaWakeLockActive = true;
          console.log('[WAKE] acquired');
        } catch (e) {
          console.warn('[WAKE] request failed', e);
        }
      }
    } catch (e) {
      console.warn('[WAKE] not supported or failed', e);
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        try { window.__truvaWakeLockActive = false; } catch (e) {}
        console.log('[WAKE] released');
      }
    } catch (e) {
      console.warn('[WAKE] release failed', e);
    }
  }, []);

  // Ensure we end any reported CallKit call (no-op if none)
  const endCallKitCall = () => {
    if (!callKitUuidRef.current) return;
    try {
      callKitSafe(async (ck) => {
        try {
          if (ck && ck.endCall) {
            await ck.endCall({ uuid: callKitUuidRef.current });
          }
        } catch (e) {
          console.warn('[CallKit] endCall failed', e);
        }
        callKitUuidRef.current = null;
      });
    } catch (e) { /* ignore */ }
  };

  // Ensure AudioContext is created/resumed inside a user gesture and expose it
  const ensureAudioStarted = useCallback(async () => {
    try {
      if (typeof window !== 'undefined') {
        if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') {
          try { await audioCtx.resume(); } catch (e) { /* ignore */ }
        }
        try { window.__truvaAudioContext = audioCtx; } catch (e) {}
      }
    } catch (e) {
      console.warn('[AUDIO] ensureAudioStarted failed:', e);
    }
  }, []);

  const handleConnectClick = useCallback(async () => {
    await ensureAudioStarted();
    // Retry Connect must fully tear down the prior Fabric client first —
    // @signalwire/js keeps a module singleton until disconnect() finishes,
    // and stale as-SAT sessionStorage causes "Requester validation failed".
    try {
      const pending = deviceRef.current?.destroy?.();
      if (pending && typeof pending.then === 'function') await pending;
    } catch (_) {}
    deviceRef.current = null;
    try { window.truvaDevice = null; } catch (_) {}
    setDeviceReady(false);
    setDeviceInitRequested(true);
    // Bump generation so the init effect re-runs even when Phone tab /
    // deviceInitRequested were already armed.
    setDeviceConnectGeneration((n) => n + 1);
  }, [ensureAudioStarted]);

  // ── APNs VoIP push token registration ──
  // VoipPushManager.swift (iOS) hands a fresh PKPushRegistry token to JS
  // via this event rather than posting it to the backend itself — JS
  // already owns authToken/fetch, duplicating that natively for one
  // endpoint isn't worth it. See plans/P52_HANDOFF_WEB_ANDROID.md.
  // Persist the token if it arrives before login so we can register later.
  useEffect(() => {
    if (!isNativePlatform || typeof window === 'undefined') return undefined;
    const register = async (token, environment) => {
      if (!token || !authToken) {
        if (token) {
          try {
            window.localStorage.setItem('truvaVoipPushToken', JSON.stringify({ token, environment }));
          } catch (_) {}
        }
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/api/push/register-device`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ device_token: token, environment, platform: 'ios-voip' }),
        });
        if (!res.ok) {
          console.warn('[VoipPush] register-device failed:', res.status);
        } else {
          console.log('[VoipPush] device token registered');
          try { window.localStorage.removeItem('truvaVoipPushToken'); } catch (_) {}
          // Remember which token this device registered so logout can drop it server-side.
          try { window.localStorage.setItem('truvaRegisteredPushToken', token); } catch (_) {}
        }
      } catch (e) {
        console.warn('[VoipPush] register-device error:', e);
      }
    };
    const onToken = (ev) => {
      const { token, environment } = ev.detail || {};
      register(token, environment);
    };
    window.addEventListener('truva:voipPushToken', onToken);
    try {
      const cached = JSON.parse(window.localStorage.getItem('truvaVoipPushToken') || 'null');
      if (cached?.token) register(cached.token, cached.environment || 'development');
    } catch (_) {}
    // Also pull directly from native: the live event above is lost if
    // PKPushRegistry.didUpdate (or its cold-launch redelivery) fires before
    // this listener is attached — a real race, not hypothetical, since the
    // WebView bundle can take longer to boot than the APNs handshake.
    callKitSafe(async (ck) => {
      const pending = await ck.getPendingVoipPushToken();
      if (pending?.token) register(pending.token, pending.environment || 'development');
    });
    return () => window.removeEventListener('truva:voipPushToken', onToken);
  }, [authToken]);

  // ── APNs *alert* push token registration (iOS) ──
  // Separate from the VoIP token above on purpose: a PushKit VoIP push may
  // only be used for a real incoming call, so voicemail / SMS / missed call /
  // AI summary / failed transfer / chat-lead alerts travel as ordinary APNs
  // alert pushes from the REGULAR token (AlertPushManager.swift). They are
  // stored as platform 'apns-alert'.
  useEffect(() => {
    if (!isNativePlatform || typeof window === 'undefined') return undefined;
    const platform = Capacitor.getPlatform?.() || '';
    if (platform !== 'ios') return undefined;
    const register = async (token, environment) => {
      if (!token || !authToken) {
        // Hold onto a token that arrives before login so it can be
        // registered as soon as there is a session.
        if (token) {
          try {
            window.localStorage.setItem('truvaApnsAlertToken', JSON.stringify({ token, environment }));
          } catch (_) {}
        }
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/api/push/register-device`, {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + authToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ device_token: token, environment, platform: 'apns-alert' }),
        });
        if (!res.ok) {
          console.warn('[AlertPush] register-device failed:', res.status);
        } else {
          console.log('[AlertPush] alert token registered');
          try { window.localStorage.removeItem('truvaApnsAlertToken'); } catch (_) {}
          try { window.localStorage.setItem('truvaRegisteredAlertToken', token); } catch (_) {}
        }
      } catch (e) {
        console.warn('[AlertPush] register-device error:', e);
      }
    };
    const onToken = (ev) => {
      const { token, environment } = ev.detail || {};
      register(token, environment);
    };
    window.addEventListener('truva:apnsAlertToken', onToken);
    try {
      const cached = JSON.parse(window.localStorage.getItem('truvaApnsAlertToken') || 'null');
      if (cached?.token) register(cached.token, cached.environment || 'development');
    } catch (_) {}
    // Cold-launch race: APNs can hand the token over before this listener
    // exists, so pull it directly from native too.
    callKitSafe(async (ck) => {
      const pending = await ck.getPendingAlertPushToken?.();
      if (pending?.token) register(pending.token, pending.environment || 'development');
    });
    return () => window.removeEventListener('truva:apnsAlertToken', onToken);
  }, [authToken]);

  // ── Notification permission status (non-prompting) ──
  // The prompt below only fires on the login path, and only reports its own
  // outcome. If the user says yes and later turns notifications off in iOS
  // Settings — or restored a backup, or the prompt was answered on a previous
  // install — the JS state would stay 'unknown' and the Settings hint would
  // never appear. So read the CURRENT authorization status without prompting,
  // on launch and whenever the app returns to the foreground.
  useEffect(() => {
    if (!isNativePlatform || typeof window === 'undefined') return undefined;
    const platform = Capacitor.getPlatform?.() || '';
    if (platform !== 'ios') return undefined;
    let cancelled = false;
    const sync = () => {
      callKitSafe(async (ck) => {
        const out = await ck.getNotificationPermissionStatus?.();
        if (cancelled || !out || !out.status) return;
        setNotificationPermission(out.status);
      });
    };
    sync();
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // ── Notification permission + alert-token registration after login ──
  // Asking at cold launch would prompt a stranger before they have an
  // account, so the prompt happens once a session exists. A denial is
  // surfaced to Settings (notificationPermission) instead of being re-asked
  // — iOS only ever shows the system prompt once anyway.
  useEffect(() => {
    if (!isNativePlatform || typeof window === 'undefined') return undefined;
    const platform = Capacitor.getPlatform?.() || '';
    if (platform !== 'ios' || !authToken) return undefined;
    let cancelled = false;
    callKitSafe(async (ck) => {
      const out = await ck.beginAlertPushAfterLogin?.();
      if (cancelled) return;
      const status = out?.status || 'unknown';
      setNotificationPermission(out?.granted === false ? 'denied' : status);
      if (out?.granted === false) {
        console.warn('[AlertPush] notification permission denied — showing Settings hint');
      }
    }).then(() => {
      // A token handed over before login is registered now.
      try {
        const cached = JSON.parse(window.localStorage.getItem('truvaApnsAlertToken') || 'null');
        if (cached?.token) {
          window.dispatchEvent(new CustomEvent('truva:apnsAlertToken', {
            detail: { token: cached.token, environment: cached.environment || 'development' },
          }));
        }
      } catch (_) {}
    });
    return () => { cancelled = true; };
  }, [authToken]);

  // ── Notification tap → the tab the alert belongs to ──
  // AlertPushManager posts truva:alertRoute with the server's `route` key
  // (sms → messages, voicemail → voicemail, missed call / AI summary /
  // failed transfer / chat lead → recents). Remounting the tab via
  // tabResetKeys lands on its root list rather than a stale drill-down.
  useEffect(() => {
    if (!isNativePlatform || typeof window === 'undefined') return undefined;
    const onRoute = (ev) => {
      const route = ev.detail?.route || 'recents';
      const target = TABS.includes(route) ? route : 'recents';
      setMoreTabsOpen(false);
      setAccountMenuOpen(false);
      setTab(target);
      if (['messages', 'recents', 'contacts', 'voicemail'].includes(target)) {
        setComposeTo(null);
        setTabResetKeys((k) => ({ ...k, [target]: (k[target] || 0) + 1 }));
      }
      console.log('[AlertPush] routed notification to tab', target);
    };
    window.addEventListener('truva:alertRoute', onRoute);
    return () => window.removeEventListener('truva:alertRoute', onRoute);
  }, []);

  // ── Push device-secret (native iOS refresh path) ──
  // VoipPushManager.swift uploads the VoIP push token natively in
  // PKPushRegistry.didUpdate so a token that changed while the WebView was
  // closed still reaches /api/push/register-device. That requires a
  // device-scoped secret stored in the Keychain; we obtain it on login and
  // hand it to native, and clear it on logout (revoking on the server too).
  useEffect(() => {
    if (!isNativePlatform || typeof window === 'undefined') return undefined;
    const platform = Capacitor.getPlatform?.() || '';
    if (!authToken) {
      if (platform === 'ios') {
        callKitSafe(async (ck) => ck.clearDeviceSecret?.());
      } else if (platform === 'android') {
        AudioRoute?.clearPushDeviceSecret?.().catch(() => {});
      }
      return undefined;
    }
    if (platform !== 'ios' && platform !== 'android') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/push/device-secret`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          console.warn('[PUSH] device-secret issue failed:', res.status);
          return;
        }
        const body = await res.json();
        if (cancelled || !body?.device_id || !body?.device_secret) return;
        if (platform === 'ios') {
          await callKitSafe(async (ck) => ck.storeDeviceSecret?.({ deviceId: body.device_id, deviceSecret: body.device_secret }));
        } else if (platform === 'android') {
          // Hand the credential to native so TruvaMessagingService.onNewToken
          // can re-register a rotated FCM token without the WebView.
          // The server only accepts "development" or "production" for this field.
          const env = /localhost:|\/\/.*ngrok/.test(API_BASE) ? 'development' : 'production';
          await AudioRoute?.storePushDeviceSecret?.({
            deviceId: body.device_id,
            deviceSecret: body.device_secret,
            apiBase: API_BASE,
            environment: env,
          });
          // Grants what Android needs for the Ooma-like guarantee: the
          // 13+ notification permission and the battery-optimisation
          // exemption (no-op when already granted/exempt).
          AudioRoute?.requestPushPermissions?.().catch((picErr) => {
            console.warn('[PUSH] requestPushPermissions failed:', picErr?.message || picErr);
          });
        }
        console.log('[PUSH] device secret stored natively');
      } catch (e) {
        console.warn('[PUSH] device-secret issue error:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [authToken]);

  // Android: keep a background poller alive so inbound rings while the
  // WebView is suspended. iOS uses VoIP push + native SIP instead.
  useEffect(() => {
    if (!isNativePlatform || !authToken || !deviceReady) return undefined;
    const platform = Capacitor.getPlatform?.() || '';
    if (platform !== 'android') return undefined;
    AudioRoute?.startInboundRingPoll?.({ apiBase: API_BASE, authToken }).catch((e) => {
      console.warn('[INCOMING] startInboundRingPoll failed:', e?.message || e);
    });
    return () => {
      AudioRoute?.stopInboundRingPoll?.().catch(() => {});
    };
  }, [authToken, deviceReady]);

  // ── Softphone always-on: force SIP re-REGISTER on resume ──
  // Product rule: never stay "Online" while unregistered after long dormancy.
  // Native SipRegistrationKeeper also recovers without opening the app; this
  // path clears the Online badge / dial gate immediately and nudges refresh.
  useEffect(() => {
    if (!isNativePlatform || !authToken) return undefined;
    let cancelled = false;

    const onResume = (source) => {
      const device = deviceRef.current;
      if (!device || typeof device.refreshRegistration !== 'function') return;
      console.warn(`[RESUME-SIP] ${source} — clearing Online and refreshing registration`);
      markOfflineSoon();
      device.refreshRegistration({ reason: source }).catch((e) => {
        console.warn('[RESUME-SIP] refresh failed:', e?.message || e);
      });
    };

    const onVis = () => {
      if (!cancelled && document.visibilityState === 'visible') onResume('visibilitychange');
    };
    document.addEventListener('visibilitychange', onVis);

    let removeCap = null;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (!cancelled && isActive) onResume('appStateChange');
    }).then((handle) => {
      removeCap = () => handle?.remove?.();
    }).catch(() => {});

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      try { removeCap?.(); } catch (_) {}
    };
  }, [authToken, callConnected]);

  // ── Auto-connect on mount ──
  // Once the user is authenticated, arm device init automatically.
  // This eliminates the manual Connect button click for the happy path.
  // Note: browsers' autoplay policies require a user gesture before an
  // AudioContext can leave the 'suspended' state. The Device itself
  // registers fine without a gesture, but actual audio playback may be
  // muted until the user clicks anywhere — the existing "Unlock Audio"
  // button (visible while AudioContext is suspended) is the manual
  // escape hatch for that. For the typical login flow the login click
  // ITSELF is enough of a user gesture for most browsers.
  useEffect(() => {
    if (!authToken || deviceInitRequested) return;
    // Same on web and native: arm the device-init effect below, which
    // fetches its own token and builds the @signalwire/js device.
    //
    // This used to fork on Capacitor.isNativePlatform() and, on native,
    // fetch /token itself and hand the result to TwilioVoicePlugin's
    // initialize() bridge — back when the native Twilio SDK owned call
    // audio and lifecycle. Calls now run through @signalwire/js inside
    // the WebView on every platform (createVoiceDevice returns a
    // SignalWireWebDevice unconditionally), so that branch was doing a
    // redundant token fetch followed by a call into a plugin that never
    // places calls. Its only real effect was the setDeviceInitRequested
    // below, which is exactly what the web branch already did.
    console.log('[INIT] Auto-arming device init (authToken present, no prior init request)');
    setDeviceInitRequested(true);
  }, [authToken, deviceInitRequested]);

  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        // Foreground after minimize: re-acquire the wake lock that the
        // browser auto-released on hidden. Also defensively restore the
        // 'Connected' status if a call is still active — this guards
        // against any background-suspend artifact where React state
        // looks stale to the user when they come back. The native call
        // path (TwilioVoicePlugin) survives backgrounding independently
        // so the audio stays alive while the WebView is suspended.
        if (authToken) {
          try {
            await fetch(`${API_BASE}/api/keepalive`, {
              headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
            });
          } catch (_) { /* ignore */ }
        }
        if (activeCall) {
          console.warn('[VISIBILITY-DIAG] visible with activeCall present — forcing status=Connected');
          await requestWakeLock();
          setStatus((cur) => (cur === 'Connected' ? cur : 'Connected'));
        }
      }
      // On hidden: do nothing. The call object, isBridging state,
      // participants polling, and all UI state are kept intact so the
      // user can jump straight back in when they re-foreground. Wake
      // lock release on minimize is a browser-driven event we don't
      // need to react to.
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [activeCall, authToken, requestWakeLock]);

  // ── Timer helpers ─────────────────────────────────
  const startTimer = useCallback(() => {
    setCallTimer(0);
    timerRef.current = setInterval(() => setCallTimer((t) => t + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  // ── Conference participant roster helpers ────────────
  const fetchParticipants = useCallback(async (callSid) => {
    if (!callSid) return;
    try {
      const url = `${API_BASE}/conference/participants?call_sid=${encodeURIComponent(callSid)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) {
        let json = null;
        try {
          json = await res.json();
        } catch (e) {
          // Empty or invalid JSON — clear roster
          setParticipants([]);
          return;
        }
        if (Array.isArray(json)) {
          // Backend may return a top-level array (legacy or empty list)
          setParticipants(json);
        } else if (json && Array.isArray(json.participants)) {
          // Modern shaped response: { participants: [...] }
          setParticipants(json.participants);
        } else {
          setParticipants([]);
        }
      } else if (res.status === 401) {
        setParticipants([]);
      } else {
        // Clear roster on other non-OK responses
        setParticipants([]);
        console.warn('[ROSTER] fetch non-OK', res.status);
      }
    } catch (e) {
      console.warn('[ROSTER] fetch failed', e);
    }
  }, [authHeaders]);

  useEffect(() => {
    let iv = null;
    let clearTimer = null;
    const sid = activeCall?.parameters?.CallSid || activeCall?.sid || activeCall?.callSid || '';
    // Only poll /conference/participants when a 3-way bridge is actually
    // in flight (isBridgingState is set true by armHandoffGrace at the
    // OK-click of the Add prompt and cleared by the event-driven release).
    // For plain 2-party calls there's no useful roster to fetch and the
    // endpoint returns either [] or a 4xx if the conference hasn't been
    // materialized yet — which surfaced as 404 noise during normal calls.
    // The bridge-window-only polling preserves the handoff-grace
    // early-release detector (which depends on roster changes) without
    // burning a request every 3s on every active call.
    if (callConnected && sid && isBridgingState) {
      fetchParticipants(sid);
      iv = setInterval(() => fetchParticipants(sid), 3000);
    } else {
      // Delay clearing the roster by 2s so the user sees the final
      // participant state briefly before the UI returns to the clean
      // dialer view. If a new call/bridge starts before the timer fires,
      // the cleanup below cancels it.
      clearTimer = setTimeout(() => setParticipants([]), 2000);
    }
    return () => {
      if (iv) clearInterval(iv);
      if (clearTimer) clearTimeout(clearTimer);
    };
  }, [callConnected, activeCall, fetchParticipants, isBridgingState]);

  // Auto-reset the 'Call ended' status to 'Ready' after 2s so the UI
  // returns to its clean idle state. Only fires if status hasn't been
  // overwritten in the meantime (e.g. user started a new call quickly).
  useEffect(() => {
    if (status !== 'Call ended') return;
    const t = setTimeout(() => {
      setStatus((cur) => (cur === 'Call ended' ? 'Ready' : cur));
    }, 2000);
    return () => clearTimeout(t);
  }, [status]);

  // Fetch contacts on login + every 60s so ParticipantRoster can resolve
  // phone numbers to saved names. Quietly no-ops if /contacts fails.
  useEffect(() => {
    if (!authToken) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const fetchContacts = async () => {
      try {
        const r = await fetch(`${API_BASE}/contacts`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
          signal,
        });
        if (!r.ok) return;
        const data = await r.json().catch(() => []);
        if (Array.isArray(data) && !signal.aborted) setContacts(data);
      } catch (e) {
        if (e.name === 'AbortError') return;
        /* ignore other errors */
      }
    };
    fetchContacts();
    const iv = setInterval(fetchContacts, 60000);
    return () => {
      controller.abort();
      clearInterval(iv);
    };
  }, [authToken]);

  // Event-driven handoff release. The lock has NO setTimeout fallback;
  // it is held until the roster confirms the bridge has transitioned.
  // Two release triggers:
  //   • count DECREASE (original leg / P2 terminated) — emits the
  //     REAL-TIME LATENCY log because we have a meaningful anchor
  //     (OK click → leg termination)
  //   • count INCREASE above baseline (new participant / P3 joined) —
  //     releases silently; no latency log because the anchor is wrong
  //     (P3 ringing time is not a bridge-speed measurement)
  useEffect(() => {
    if (!handoffActiveRef.current) return;
    const current = participants.length;
    const baseline = baselinePCountRef.current;
    if (current < baseline) {
      const latencyMs = Date.now() - handoffClickTimeRef.current;
      const latencySec = (latencyMs / 1000).toFixed(2);
      console.log(`REAL-TIME LATENCY: ${latencySec}s (OK Click to Leg Termination)`);
      console.log(
        `[CALL ADD] HANDOFF GRACE released: roster shrank ${baseline} → ${current} ` +
        `(original leg terminated; ${latencyMs}ms after OK click)`
      );
      clearHandoffGrace('original_leg_left');
    } else if (current > baseline) {
      const sinceArm = Date.now() - handoffClickTimeRef.current;
      console.log(
        `[CALL ADD] HANDOFF GRACE released: roster grew ${baseline} → ${current} ` +
        `(new participant joined; ${sinceArm}ms after OK click — no latency anchor for this path)`
      );
      clearHandoffGrace('new_participant_joined');
    }
  }, [participants]);

  // ── Request notification permission on mount ──────────
  useEffect(() => {
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch (e) {
      console.warn('Notification permission request failed:', e);
    }
  }, []);

  // ── Initialize SignalWire Device on mount (deferred until user gesture) ─────────────
  useEffect(() => {
    if (!authToken) {
      // stop sentinel if no token
      if (sentinelRef.current) {
        try { sentinelRef.current.postMessage('stop'); } catch (e) {}
      }
      if (keepaliveIntervalRef.current) {
        clearInterval(keepaliveIntervalRef.current);
        keepaliveIntervalRef.current = null;
      }
      return;
    }

    // Update/start sentinel worker or fallback interval to keep session alive
    if (typeof window !== 'undefined' && 'Worker' in window) {
      try {
        if (!sentinelRef.current) {
          sentinelRef.current = new Worker('/session-worker.js');
          sentinelRef.current.onmessage = (ev) => console.debug('[Sentinel]', ev.data);
        }
        sentinelRef.current.postMessage({ token: authToken, apiBase: API_BASE || '' });
        sentinelRef.current.postMessage('start');
      } catch (e) {
        console.warn('Session sentinel worker failed, falling back to main-thread keepalive', e);
      }
    }
    // Always ping from the page too. Mobile WebViews freeze Workers in
    // the background; a worker-only keepalive is why sessions died overnight.
    if (keepaliveIntervalRef.current) clearInterval(keepaliveIntervalRef.current);
    async function keepalive() {
      try {
        await fetch(`${API_BASE}/api/keepalive`, { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } });
      } catch (e) { /* ignore */ }
    }
    keepalive();
    keepaliveIntervalRef.current = setInterval(keepalive, 5 * 60 * 1000);

    const init = async () => {
      try {
        console.log('[CONFIG] API_BASE resolved to:', API_BASE);
        console.log('[CONFIG] window.location.origin:', (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : 'N/A');
        console.log('[INIT] Step 1: Fetching /numbers...');
        // Fetch available phone numbers
        const numRes = await fetch(`${API_BASE}/numbers`, { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } });
        let nums = [];
        if (numRes.ok) {
          nums = await numRes.json();
          setPhoneNumbers(nums);
          const owned = new Set(nums.map((n) => n.phone_number));
          let pick = '';
          const saved = loadSelectedLineForUser(user);
          if (saved && owned.has(saved)) pick = saved;
          // One line → select it. Multiple → restore this user's last pick
          // only (never a shared org/device default).
          if (!pick && nums.length === 1) pick = nums[0].phone_number;
          setSelectedNumber(pick);
          if (pick) saveSelectedLineForUser(user, pick);
        }
        console.log('[INIT] Step 1 done: /numbers status=', numRes.status, 'count=', nums.length);

        console.log('[INIT] Step 2: Fetching /token...');
        setStatus('Fetching token…');
        const tokenUrl = `${API_BASE}/token`;
        console.log('[FETCH]', tokenUrl);
        const res = await fetch(tokenUrl, { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } });
        if (!res.ok) {
          throw new Error(`Token request failed (${res.status})`);
        }
        const data = await res.json();
        console.log('[INIT] Step 2 done: token received, length=', data.token?.length);
        if (data.dev_token) {
          throw new Error('Telephony credentials misconfigured on server (dev token returned)');
        }

        // NOTE: Do NOT call getUserMedia here. The Twilio SDK calls its own
        // getUserMedia internally when connecting a call. Pre-calling it on
        // iOS WKWebView holds the mic exclusively, causing Twilio's internal
        // stream to receive silence (constant-audio-input-level = 0).
        // The mic permission will be requested by the SDK on first call.

        console.log('[INIT] Step 3: Creating SignalWire Device...');
        let deviceSetupError = null;
        const device = await createVoiceDevice(data.token, {
          logLevel: 'warn',
          tokenType: data.token_type || 'sat',
          spaceUrl: data.space_url,
          // Required by the native SIP path, which fetches this user's SIP
          // credential from /api/sip/credential at construction time. The
          // WebView device gets these later via connect(), so leaving them
          // out only broke native — with "apiBase required to fetch SIP
          // credentials" before a call could be placed.
          apiBase: API_BASE,
          authToken,
          onRefreshToken: async () => {
            const r = await fetch(tokenUrl, {
              headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
            });
            if (!r.ok) throw new Error(`Token refresh failed (${r.status})`);
            const refreshed = await r.json();
            return refreshed.token;
          },
        });
        console.log('[INIT] Step 3 done: Device created (native=', isNativePlatform, ')');

        device.on('registered', () => {
          console.log('[INIT] Device registered successfully');
          markOnline();
        });

        device.on('unregistered', () => {
          console.log('[INIT] Device unregistered — phone line not ready');
          markOfflineSoon();
        });

        device.on('error', (err) => {
          deviceSetupError = err;
          const detail = err?.message || err?.code || String(err);
          console.error('Device error:', detail, err?.code, JSON.stringify(err));
          // SIP registration failures fire here independently of (and much
          // sooner than — 3s vs up to 17s) device.register()'s own credential
          // retry/backoff below. Showing the raw message here beat that retry
          // to the screen, flashing "SIP registration Failed: Unauthorized"
          // on a credential the registrar just hadn't caught up on yet, then
          // silently overwriting it once 'registered' fired moments later.
          // register()'s catch block (below) already shows a friendly
          // message if the retries genuinely exhaust, so stay quiet here.
          if (String(detail).includes('SIP registration')) return;
          setStatus('Error: ' + detail);
        });

        device.on('incoming', (call) => {
          console.log('[INCOMING] Incoming call from:', call.parameters?.From);

          // The in-app Accept/Decline overlay this drives is only needed
          // for the WebView call path. When native voice is enabled, the
          // real system CallKit banner (CallKitManager.reportIncomingCall,
          // triggered from LinphoneManager's own IncomingReceived handler)
          // already gives the user a way to answer/decline — showing both
          // is redundant and confusing (two "Accept/Decline" UIs stacked
          // for the same call).
          if (!isNativeVoiceEnabled()) {
            setIncomingCall(call);
          }
          setStatus('Incoming call…');

          // Report to CallKit so iOS owns audio routing — but ONLY for the
          // WebView call path. When native voice is enabled, LinphoneManager
          // already owns the AVAudioSession directly and the route defaults
          // to the earpiece on its own (verified on device). Reporting to
          // CallKit here anyway still activates the SAME shared
          // AVAudioSession CallKitManager.didActivate then runs its
          // deactivate/reactivate toggle against — a workaround written for
          // WebKit's separate-process audio engine. linphone's audio unit
          // runs IN this process, so that forced toggle collides with a
          // live call instead of "waking up" an unrelated engine: observed
          // on device, the toggle logs "Session deactivation failed" and
          // the call connects with no audio in either direction.
          if (!isNativeVoiceEnabled()) {
            callKitSafe(async (ck) => {
              const r = await ck.reportIncomingCall({
                handle: call.parameters?.From || 'Unknown',
              });
              if (r?.uuid) callKitUuidRef.current = r.uuid;
            });
          }

          // Ring + vibrate.
          //
          // The audible ring is skipped for a native call: LinphoneManager
          // already rings via AudioServicesPlaySystemSound on
          // IncomingReceived, and running both simultaneously means two
          // independent, competing ring mechanisms on one event. Confirmed
          // on device this was actively causing confusion diagnosing a
          // separate bug — the JS oscillator's AudioContext was observed in
          // an 'interrupted' state (a third state this code never checked
          // for, alongside 'suspended'/'running') during a real call,
          // silently producing no sound regardless of anything else fixed
          // here. Vibrate and the visual notification are unaffected —
          // neither depends on AudioContext state, and there's no harm in
          // both paths providing them.
          if (!isNativeVoiceEnabled()) {
            try { ringtoneRef.current = playRingtone(); } catch (e) { console.warn('Ringtone failed:', e); }
          }
          vibrate([500, 300, 500, 300, 500, 300, 500, 300, 500]);
          sendNotification('Incoming Call', call.parameters?.From || 'Unknown caller');

          // Only the native SIP path needs this: it's answered from the
          // system CallKit banner, entirely outside any JS UI interaction
          // (there's no in-app Accept tap to drive setActiveCall/setStatus
          // the way the WebView path's handleAccept does). Without this,
          // NativeCall's own 'accept' event — emitted from nativeVoiceClient
          // .js's _onState() once the native call reaches Connected/
          // StreamsRunning — had no listener at all, so the screen never
          // left "Incoming call…" even once the call was genuinely live.
          if (isNativeVoiceEnabled()) {
            call.on('accept', () => {
              console.log('[INCOMING] Native call accepted/connected');
              setActiveCall(call);
              setCallConnected(true);
              setCallStatus('open');
              setStatus('Connected');
              try { requestWakeLock(); } catch (e) {}
              startTimer();
            });
          }

          call.on('cancel', () => {
            console.log('[INCOMING] Call cancelled');
            stopRingtone(ringtoneRef.current);
            ringtoneRef.current = null;
            vibrate(0);
            setIncomingCall(null);
            setStatus('Missed call');
            endCallKitCall();
          });

          call.on('disconnect', async () => {
            // CANARY — see outbound branch for explanation.
            console.warn('[INCOMING-DIAG] Disconnect event fired :: handoffActive=' + handoffActiveRef.current);
            // Handoff grace short-circuit — see outbound branch for the
            // full rationale. /call/add on an inbound call hits the same
            // redirect race; the grace window swallows it cleanly.
            if (handoffActiveRef.current) {
              const sinceArm = Date.now() - handoffArmedAtRef.current;
              console.warn(
                `[INCOMING-DIAG] Disconnect IGNORED — HANDOFF GRACE active (${sinceArm}ms since arm, ` +
                `event-driven release pending); allowing conference bridge to stabilize`
              );
              setStatus('Connected');
              return;
            }
            // Same false-disconnect guard as the outbound flow — if a
            // recent backend op (Add/Transfer) could have caused a
            // transient SDK event, verify with the backend before
            // tearing down.
            try {
              const stillAlive = await verifyCallStillAlive(call.parameters?.CallSid);
              console.warn(`[INCOMING-DIAG] verifyCallStillAlive returned ${stillAlive}`);
              if (stillAlive) {
                console.warn('[INCOMING-DIAG] Disconnect IGNORED — backend reports call still in-progress');
                setStatus('Connected');
                return;
              }
            } catch (e) {
              console.warn('[INCOMING-DIAG] verifyCallStillAlive threw', e);
              /* fall through to teardown */
            }
            console.warn('[INCOMING-DIAG] Call disconnected (confirmed, tearing down) — calling setStatus(Call ended)');
            stopRingtone(ringtoneRef.current);
            ringtoneRef.current = null;
            vibrate(0);
            setIncomingCall(null);
            setActiveCall(null);
            setIsMuted(false);
            setIsOnHold(false);
            stopTimer();
            setStatus('Call ended');
            endCallKitCall();
            console.warn('[INCOMING-DIAG] teardown complete');
          });
        });

        device.on('tokenWillExpire', async () => {
          try {
            await refreshDeviceToken(authToken, device);
          } catch (e) {
            console.warn('[TruvaSentinel] tokenWillExpire refresh failed', e);
          }
        });

        console.log('[INIT] Step 4: Registering device...');
        // Register only after an explicit user gesture to ensure AudioContext is resumed
        // and Twilio SDK does not create/resume audio outside a gesture (avoids autoplay policy errors).
        if (deviceInitRequested || phoneTabSelectedByUser) {
          try {
            await device.register();
            console.log('[INIT] Step 4 done: Device registered');
          } catch (registerErr) {
            const err = deviceSetupError || registerErr;
            const detail = err?.message || err?.code || (err ? String(err) : 'register failed');
            throw new Error(detail);
          }
        } else {
          // Defer registration until user interacts with the UI (Connect button or Phone tab click).
          console.log('[INIT] Device registration deferred until user gesture');
        }
        deviceRef.current = device;
        // expose device globally so native->JS bridge (AuthContext) can update it
        try { window.truvaDevice = device; } catch (e) {}
      } catch (err) {
        const detail = err?.message || err?.code || (err ? String(err) : 'unknown error');
        console.error('[INIT] FAILED at error:', detail, err?.code, err?.name, typeof err);

        // A stale/expired authToken makes /numbers and /token return 401.
        // Without this the app only rendered "Init failed: Token request
        // failed (401)" and stopped: localStorage still held the dead token,
        // so it considered the user logged in, never re-prompted, and the
        // user was stranded with no way to recover except clearing app data.
        // The outbound-call handler already recovers from exactly this, so
        // mirror it here — session expiry and backend restarts both land in
        // this path, not just after a reinstall.
        if (detail.includes('401')) {
          console.warn('[INIT] Auth token rejected (401) — clearing session and returning to login');
          setAuthToken('');
          setDeviceReady(false);
          setDeviceInitRequested(false);
          if (deviceRef.current) {
            try { deviceRef.current.destroy({ clearCredentials: true }); } catch (e) {}
            deviceRef.current = null;
          }
          // Clear user too, matching handleLogout — leaving stale user data
          // behind would keep a dead identity cached across the re-login.
          setUser(null);
          clearListCache();
          localStorage.removeItem('authToken');
          BiometricSecure.clearToken().catch(() => {}); // session expired -> drop secure copy
          localStorage.removeItem('user');
          setStatus('Session expired — please sign in again');
          return;
        }

        if (String(detail).includes('Requester validation failed')) {
          setStatus('Init failed: Requester validation failed — tap Retry Connect (or hard-refresh)');
          return;
        }

        // SIP registration failures (e.g. the registrar's brief credential
        // propagation window) are already retried with backoff inside
        // nativeVoiceClient.register() — if we land here, those retries
        // genuinely failed. A paying customer shouldn't see raw SIP jargon.
        if (String(detail).includes('SIP registration')) {
          setStatus("Couldn't connect to the phone line — tap Retry Connect to try again");
          return;
        }

        setStatus('Init failed: ' + detail);
      }
    };

    // Only initialize SignalWire Device after an explicit user gesture (Connect) or
    // when the user explicitly selected the Phone tab.
    //
    // Also skip re-running this entirely while a call is active — this
    // effect's deps re-firing mid-call (see the cleanup's comment below)
    // would otherwise both destroy the live device AND immediately
    // re-init/re-register a new one out from under the active call.
    if (activeCallRef.current) {
      console.warn('[INIT] Device init/re-init skipped — call active');
    } else if (deviceInitRequested || phoneTabSelectedByUser) {
      init();
    } else {
      console.log('[INIT] Device init deferred until user clicks Connect or selects Phone tab');
    }

    return () => {
      // This cleanup destroys the device whenever one of this effect's own
      // deps changes (authToken/deviceInitRequested/phoneTabSelectedByUser
      // — NOT activeCall, which isn't in the dep array). Confirmed on
      // device: something outside this effect (observed after changing
      // Live Translate's language mid-call) re-triggered it while a native
      // SIP call was actively StreamsRunning, tearing down and
      // unregistering the whole SIP device out from under a live call —
      // the caller was left connected with no one there, and the
      // subsequent re-register even failed a few times (unregister() also
      // clears the Keychain-stored credential, forcing a rotation that
      // raced its own propagation). The `activeCall` closed over here is
      // stale (it isn't a dep of this effect, so it reflects whatever it
      // was when the effect last ran, not now) — activeCallRef always
      // holds the live value, so use that to actually guard the destroy.
      const hasActiveCall = !!activeCallRef.current;
      // deviceInitRequested/phoneTabSelectedByUser below are this closure's
      // STALE, as-of-previous-render values — deviceWantedRef.current is
      // written unconditionally every render (see its declaration), so by
      // the time this cleanup runs it already reflects the values for the
      // render that's about to commit. If a device is still wanted (e.g.
      // phoneTabSelectedByUser flipping true right after deviceInitRequested
      // already started one, or deviceConnectGeneration bumping on Retry
      // Connect), skip the destroy entirely — native register() already
      // clears stale accounts/authinfo before creating a new one, so tearing
      // down here is unnecessary and was the actual cause of "Registration
      // disabled": the destroy's unregister() raced the in-flight register()
      // from the render that's still resolving, disabling it right after it
      // started. Confirmed on device (2026-08-26): SIP kept reporting
      // "Registration disabled" on every login/retry, tracked via Safari Web
      // Inspector to unregister() firing immediately after register().
      const stillWanted = deviceWantedRef.current;
      try {
        console.warn(
          `[INIT-CLEANUP] device.destroy() ${hasActiveCall ? 'SKIPPED (call active)' : stillWanted ? 'SKIPPED (still wanted)' : 'invoked'} ` +
          `at ${new Date().toISOString()} deps: authToken=${authToken ? 'present' : 'empty'} ` +
          `deviceInitRequested=${deviceInitRequested} ` +
          `phoneTabSelectedByUser=${phoneTabSelectedByUser}`
        );
      } catch (e) {}
      if (hasActiveCall || stillWanted) return;
      // If auth is gone this is a session wipe → clear SIP creds.
      // Otherwise leave native registration alive (product rule).
      deviceRef.current?.destroy?.({ clearCredentials: !authToken });
      stopTimer();
      try { window.truvaDevice = null; } catch (e) {}
    };
  }, [authToken, stopTimer, deviceInitRequested, phoneTabSelectedByUser, deviceConnectGeneration]);

  // Fabric SAT browsers cannot be rung via LaML. Poll for pending inbound
  // and show the same incoming UI. Warm the mic + reserve the join claim
  // during ring; dial the conference only on Accept so waitUrl/audio does
  // not bridge under the Accept/Decline overlay.
  // Also shared by VoIP/FCM wake events (truva:voipIncoming / truva:fcmInbound).
  const beginSoftphoneInbound = useCallback((data, { autoAccept = false, nativeWake = false } = {}) => {
    if (!data || incomingCall || activeCall || inboundDismissingRef.current) return;
    // Start as early as possible — every extra second here is a second
    // Chromium's P2P networking service has to stabilize before Accept
    // actually needs it. See warmIceNetworking's own comment for why.
    warmIceNetworking();
    const synthetic = {
      parameters: {
        From: data.from || 'Unknown',
        CallSid: data.pstn_call_sid || '',
      },
      _softphoneInbound: true,
      // Set only when this ring arrived via a native OS wake (Android
      // notification action / iOS VoIP push) rather than the foreground
      // poll — the native banner already owns Accept/Decline in that case,
      // so the in-app overlay renders nothing for it (see IncomingCall
      // callerInfo below). Foreground-detected rings still show the overlay
      // since there's no competing OS UI for those.
      _nativeWake: nativeWake,
      _inboundMeta: data,
      _prejoinPromise: null,
      accept: async () => {},
      reject: async () => {},
      on: () => {},
      status: () => 'pending',
    };
    // On iOS native voice, CallKit+SIP owns background media — skip WebRTC
    // prejoin (CallKit mutes WKWebView capture). Foreground web still reserves.
    const useNativeSip = isNativeVoiceEnabled();
    if (!useNativeSip && authToken) {
      synthetic._prejoinPromise = (async () => {
        try {
          if (!window.__truvaWarmMic) {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            window.__truvaWarmMic = stream;
          }
        } catch (e) {
          console.warn('[INCOMING] mic warm failed:', e?.message || e);
        }
        // Do NOT call wake-ack from the web softphone path — that REST-dials
        // native SIP into the conference and bridges audio while this UI still
        // shows Accept/Decline (and can pop the pending ring before Accept).
        const pre = await fetch(`${API_BASE}/api/softphone/prejoin-inbound`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({ wake_token: data.wake_token || '' }),
        });
        const body = await pre.json().catch(() => ({}));
        if (!pre.ok) throw new Error(body.error || `prejoin-inbound HTTP ${pre.status}`);
        // Reserve only — dial happens on Accept so PSTN stays on hold music.
        return { call: null, body };
      })().catch((e) => {
        console.warn('[INCOMING] prejoin reserve failed (Accept will dial):', e?.message || e);
        return null;
      });
    } else if (useNativeSip && data.wake_token && authToken) {
      // Native CallKit path may still need wake-ack to re-INVITE SIP.
      fetch(`${API_BASE}/api/softphone/wake-ack`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wake_token: data.wake_token }),
      }).catch(() => {});
    }
    setIncomingCall(synthetic);
    setStatus('Incoming call…');
    try { ringtoneRef.current = playRingtone(); } catch (e) { console.warn('Ringtone failed:', e); }
    vibrate([500, 300, 500, 300, 500, 300, 500, 300, 500]);
    {
      const lineLabel = permittedIncomingLineLabel(data.to, phoneNumbers);
      const fromLabel = data.from || 'Unknown caller';
      sendNotification(
        lineLabel ? `Incoming on ${lineLabel}` : 'Incoming Call',
        fromLabel,
      );
    }
    // Local ring ceiling — backend dumps to VM ~28s; dismiss UI even if the
    // pending-inbound poll is throttled (background tab) so Accept/Decline
    // does not stick after the caller already hit voicemail.
    if (inboundExpireTimerRef.current) {
      clearTimeout(inboundExpireTimerRef.current);
      inboundExpireTimerRef.current = null;
    }
    const ttlMs = Math.max(
      5_000,
      Math.min(90_000, ((Number(data.expires_in) || 28) + 2) * 1000),
    );
    const ringPstn = data.pstn_call_sid || '';
    inboundExpireTimerRef.current = setTimeout(() => {
      inboundExpireTimerRef.current = null;
      const cur = incomingCallRef.current;
      if (!cur?._softphoneInbound) return;
      if (ringPstn && cur.parameters?.CallSid && cur.parameters.CallSid !== ringPstn) return;
      console.warn('[INCOMING] local ring timeout — dismissing softphone UI');
      stopRingtone(ringtoneRef.current);
      ringtoneRef.current = null;
      vibrate(0);
      releaseWarmMic();
      setIncomingCall(null);
      setStatus('Missed call → voicemail');
    }, ttlMs);
    if (autoAccept) {
      setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent('truva:softphoneCallKitAnswer')); } catch (_) {}
      }, 50);
    }
  }, [incomingCall, activeCall, authToken, phoneNumbers]);

  useEffect(() => {
    // Poll even if the SignalWire device is still connecting — otherwise a
    // foreground SSO softphone never sees pending-inbound while Offline.
    //
    // Native SIP calls arrive directly via the device's own 'incoming'
    // event — skip this poll entirely when native voice is active.
    if (!authToken || activeCall || isNativeVoiceEnabled()) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        if (inboundDismissingRef.current) return;
        const res = await fetch(`${API_BASE}/api/softphone/pending-inbound`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'ngrok-skip-browser-warning': 'true',
          },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const current = incomingCallRef.current;
        if (current?._softphoneInbound) {
          // Caller hung up / backend cleared the ring — dismiss Accept/Decline.
          if (!data?.pending) {
            if (inboundExpireTimerRef.current) {
              clearTimeout(inboundExpireTimerRef.current);
              inboundExpireTimerRef.current = null;
            }
            stopRingtone(ringtoneRef.current);
            ringtoneRef.current = null;
            vibrate(0);
            releaseWarmMic();
            setIncomingCall(null);
            setStatus('Call ended');
          }
          return;
        }
        if (!data?.pending || cancelled || inboundDismissingRef.current) return;
        beginSoftphoneInbound(data);
      } catch (e) {
        // ignore transient poll errors
      }
    };
    const id = setInterval(poll, 2000);
    poll();
    const onVis = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [authToken, activeCall, beginSoftphoneInbound]);

  // Native wake paths → same softphone UI / accept flow.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onWake = (ev) => {
      const detail = ev.detail || {};
      if (!detail.from && !detail.pstn_call_sid) return;
      // The same call can arrive twice (the native announcement and the pull below). Handle each
      // (call, autoAccept) once so Accept is never driven twice.
      const wakeKey = `${detail.pstn_call_sid || detail.from}:${detail.autoAccept === true}`;
      if (lastWakeKeyRef.current === wakeKey) return;
      lastWakeKeyRef.current = wakeKey;
      // iOS native: VoipPushManager dispatches this on every VoIP push
      // unconditionally, but CallKit + LinphoneManager already answer the
      // SIP INVITE directly in Swift — routing it into the same
      // WebView-only synthetic-call flow here would race the real native
      // call the same way the pending-inbound poll did (see that effect's
      // comment above).
      if (isNativeVoiceEnabled()) return;
      // The foreground poll (500ms) usually beats this native-wake event
      // (native Android poll is 2000ms) to setting incomingCall — when that
      // happens, beginSoftphoneInbound's own-call guard makes it a no-op,
      // which would silently swallow a tap on the notification's Accept
      // action (its autoAccept dispatch lives inside that guarded call).
      // Drive the already-showing call directly instead in that case.
      if (incomingCallRef.current?._softphoneInbound) {
        if (detail.autoAccept) handleAcceptRef.current?.();
        return;
      }
      beginSoftphoneInbound({
        from: detail.from || 'Unknown',
        to: detail.to || '',
        conf_name: detail.conf_name || '',
        pstn_call_sid: detail.pstn_call_sid || '',
        dial_address: detail.dial_address || '',
        wake_token: detail.wake_token || '',
        pending: true,
      }, { nativeWake: true, autoAccept: detail.autoAccept === true });
    };
    const onCkAnswer = () => {
      // Softphone CallKit answer (non-native-SIP): drive the same Accept path.
      if (incomingCallRef.current?._softphoneInbound) {
        handleAcceptRef.current?.();
      }
    };
    const onCkReject = () => {
      if (incomingCallRef.current?._softphoneInbound) {
        handleRejectRef.current?.();
      }
    };
    window.addEventListener('truva:voipIncoming', onWake);
    window.addEventListener('truva:fcmInbound', onWake);
    // Cold start: the native announcement above may have fired before this listener existed.
    // Android keeps the launching call; pull it now (no-op on iOS / web / nothing pending).
    try {
      AudioRoute?.getPendingIncoming?.()
        .then((r) => { if (r && (r.pstn_call_sid || r.from)) onWake({ detail: r }); })
        .catch(() => {});
    } catch (_) { /* plugin missing on older native builds */ }
    window.addEventListener('truva:softphoneCallKitAnswer', onCkAnswer);
    window.addEventListener('truva:softphoneCallKitReject', onCkReject);
    return () => {
      window.removeEventListener('truva:voipIncoming', onWake);
      window.removeEventListener('truva:fcmInbound', onWake);
      window.removeEventListener('truva:softphoneCallKitAnswer', onCkAnswer);
      window.removeEventListener('truva:softphoneCallKitReject', onCkReject);
    };
  }, [beginSoftphoneInbound, incomingCall]);

  // ── Dialpad digit press ────────────────────────────────
  const handleDigit = (digit) => {
    setDialNumber((prev) => `${prev || ''}${digit}`);
    // If there's an active call, send DTMF and echo it on the mid-call pad.
    if (activeCall) {
      if (isOnHold) {
        console.log('[DTMF] ignored — call is on hold');
        return;
      }
      activeCall.sendDigits(digit);
      setDtmfEcho((prev) => (prev + digit).slice(-24));
      // A digit on the leg is the whisper answer too — retire the gate banner.
      if (transferGate) clearTransferGate();
    }
  };

  // ── Outbound call ──────────────────────────────────────
  const handleCall = async (overrideNumber) => {
    const destRaw = readDialDestination(overrideNumber, numberRef);
    if (!destRaw) return;
    const checked = validateUsDialNumber(destRaw);
    if (!checked.ok) {
      setDialNumber(destRaw);
      setStatus(checked.error);
      return;
    }
    const dest = checked.e164;
    if (!selectedNumber) {
      setStatus('Select an outbound caller number');
      return;
    }
    const device = deviceRef.current;
    if (!device) {
      setStatus('Device not ready');
      return;
    }
    if (!deviceReady) {
      setStatus('Phone line not ready — wait for it to connect');
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      // ensure UI reflects destination
      setDialNumber(destRaw);
      setStatus('Fetching call token…');
      // Fetch a fresh Twilio token from backend before each call.
      // Add abort timeout so a hung connection doesn't strand the UI forever.
      const res = await fetch(`${API_BASE}/token`, {
        headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        throw new Error(`Token request failed (${res.status})`);
      }
      const data = await res.json();
      const freshToken = data.token;
      if (!freshToken) {
        throw new Error('No token received from backend');
      }

      setStatus('Calling ' + dest + '…');
      setCallStatus('connecting');
      recentBackendOpRef.current = Date.now();
      const call = await device.connect({
        params: { To: dest, CallerId: selectedNumber },
        token: freshToken,
        apiBase: API_BASE,
        authToken,
      });

      call.on('ringing', () => {
        console.log('[CALL] Ringing...');
        setStatus('Ringing ' + dest + '…');
        setCallStatus('ringing');
      });

      call.on('accept', () => {
        console.log('[CALL] Accepted/Connected — audio should be flowing');
        console.log('[CALL] Call status:', call.status?.());
        console.log('[CALL] Call direction:', call.direction);
        setStatus('Connected');
        setCallStatus('open');
        setCallConnected(true);  // unlocks Hold / 3-Way / Transfer
        try { requestWakeLock(); } catch (e) {}
        startTimer();
      });

      // Second-call interruption / audio-focus loss / call waiting: the call is
      // on hold, NOT over. Show the held state, never disconnect, and let the
      // Hold/Resume button bring it back. Mirrors nativeVoiceClient's callHeld/
      // Paused handling and the web ICE/track interruption guards below.
      call.on('hold', () => {
        console.log('[CALL] On hold (interruption)');
        setIsOnHold(true);
        setStatus('On hold');
      });
      call.on('unhold', () => {
        console.log('[CALL] Resumed');
        setIsOnHold(false);
        setStatus(callConnectedRef.current ? 'Connected' : 'Connecting…');
      });

      // A disconnect arrived while the conference-join grace was still open,
      // so voiceClient deferred it rather than guessing. The grace exists to
      // ride out the spurious disconnect the SDK fires while this leg is
      // redirected into the conference — but the same window was also
      // swallowing real far-end hangups, leaving the UI on "Connected" with
      // the timer running until some later event happened to land after the
      // window closed (~20s of dead air, reproduced on device).
      //
      // The backend can tell the two apart, so ask it: mid-join the leg is
      // still in-progress; a real hangup has already completed it.
      call.on('graceDisconnect', async () => {
        const alive = await probeCallAlive(call.parameters?.CallSid);
        if (alive === null) {
          // Probe inconclusive — leave the call up. If it really is gone the
          // post-grace disconnect path still tears it down, so the worst case
          // is the old behaviour rather than a wrongly-killed call.
          console.log('[CALL] Grace-window disconnect — backend probe inconclusive, leaving call up');
          return;
        }
        if (alive) {
          console.log('[CALL] Grace-window disconnect ignored — backend reports call still in-progress (real mid-join blip)');
          setStatus('Connecting…');
          return;
        }
        console.log('[CALL] Grace-window disconnect confirmed real by backend — tearing down now');
        call.finishConfirmed?.();
      });

      call.on('disconnect', async () => {
        // CANARY — if you don't see this line in console, the bundle is stale.
        console.log(
          `[CALL] Disconnected event fired :: handler-version=2026-05-22-guard-v2 ` +
          `call.parameters.CallSid=${call.parameters?.CallSid || 'UNDEFINED'} ` +
          `call.status=${typeof call.status === 'function' ? call.status() : 'N/A'}`
        );
        // Handoff grace short-circuit: after a successful /call/add the
        // backend redirects this SDK leg into the conference (~1s). The
        // SDK can fire a spurious `disconnect` mid-redirect even though
        // the conference bridge is healthy. Skip the verify probe and
        // ignore the event entirely until grace expires.
        if (handoffActiveRef.current) {
          const sinceArm = Date.now() - handoffArmedAtRef.current;
          console.log(
            `[CALL] Disconnect IGNORED — HANDOFF GRACE active (${sinceArm}ms since arm, ` +
            `event-driven release pending); allowing conference bridge to stabilize`
          );
          setStatus('Connected');
          return;
        }
        if (call._viaConference && call._joinGraceUntil && Date.now() < call._joinGraceUntil) {
          // Don't take the grace window's word for it — ask the backend.
          // Blindly ignoring every disconnect for the whole window also
          // swallowed genuine far-end hangups (see the graceDisconnect
          // handler below for the full rationale).
          const aliveDuringGrace = await probeCallAlive(call.parameters?.CallSid);
          if (aliveDuringGrace === null || aliveDuringGrace) {
            console.log('[CALL] Disconnect IGNORED — conference join grace (backend still reports call alive)');
            setStatus('Connecting…');
            return;
          }
          console.log('[CALL] Disconnect during join grace, but backend reports call gone — tearing down');
        }
        // False-disconnect guard: if a recent backend op (Add/Transfer)
        // could have caused a transient SDK signaling event, verify with
        // the backend before tearing down. If Twilio still reports the
        // call as 'in-progress', the audio is alive and we should ignore
        // this disconnect event.
        try {
          const stillAlive = await verifyCallStillAlive(call.parameters?.CallSid);
          if (stillAlive) {
            console.log('[CALL] Disconnect IGNORED — backend reports call still in-progress (false disconnect after recent backend op)');
            setStatus('Connected'); // restore the UI to "Connected"
            return;
          }
        } catch (e) { /* fall through to teardown */ }
        console.log('[CALL] Disconnected (confirmed, tearing down)');
        try { releaseWakeLock(); } catch (e) {}
        const userHangup = Date.now() - hangupInitiatedAtRef.current < 5000;
        const endedStatus = (!userHangup && !callConnectedRef.current)
          ? UNREACHABLE_MESSAGE
          : 'Call ended';
        setActiveCall(null);
        setCallConnected(false);
        setCallStatus('closed');
        setIsMuted(false);
        setIsSpeaker(false);
        setIsOnHold(false);
        stopTimer();
        setStatus(endedStatus);
      });

      call.on('error', (err) => {
        console.error('[CALL] Error:', err?.message, err?.code, err?.twilioError);
        // Twilio error 31005 (ConnectionError) fires at HANGUP as a normal
        // side-effect of WebRTC connection teardown. Suppress it within
        // 5 s of a user-initiated hangup to avoid overwriting "Call ended"
        // with "Call error: Connection Error" in the status bar, and to
        // prevent clearing already-nulled call state a second time.
        if (err?.code === 31005 && Date.now() - hangupInitiatedAtRef.current < 5000) {
          console.warn('[CALL] Suppressing expected 31005 ConnectionError at HANGUP (within 5s of user End)');
          return;
        }
        setStatus(UNREACHABLE_MESSAGE);
        try { call.disconnect(); } catch (e) {}
        setActiveCall(null);
        setCallConnected(false);
        setCallStatus('closed');
        setIsOnHold(false);
        stopTimer();
      });

      call.on('warning', (name, data) => {
        console.warn('[CALL] Warning:', name, data);
      });

      call.on('warning-cleared', (name) => {
        console.log('[CALL] Warning cleared:', name);
      });

      setActiveCall(call);

      // Events can fire during await device.connect() before handlers attach (fast PSTN).
      const liveStatus = typeof call.status === 'function' ? call.status() : call._status;
      if (liveStatus === 'open') {
        setCallStatus('open');
        setCallConnected(true);
        setStatus('Connected');
        try { requestWakeLock(); } catch (e) {}
        if (!timerRef.current) startTimer();
      } else if (liveStatus === 'ringing') {
        setCallStatus('ringing');
        setStatus('Ringing ' + dest + '…');
      }

      // Conference outbound: ringing may have fired before handlers attached.
      if (call.status?.() === 'ringing' && callStatus !== 'ringing') {
        setCallStatus('ringing');
        setStatus('Ringing ' + dest + '…');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      const msg = err.message || 'Unknown error';
      // If auth token is stale (backend restart cleared sessions),
      // clear auth state and redirect to login so the user isn't stranded.
      if (/\b401\b/.test(msg) || /unauthorized/i.test(msg) || /session expired/i.test(msg)) {
        setStatus('Error: ' + msg);
        console.warn('[CALL] Auth token expired — clearing session and redirecting to login');
        setAuthToken(null);
        setDeviceReady(false);
        setDeviceInitRequested(false);
        if (deviceRef.current) {
          try { deviceRef.current.destroy({ clearCredentials: true }); } catch (e) {}
          deviceRef.current = null;
        }
        clearListCache();
        localStorage.removeItem('authToken');
        BiometricSecure.clearToken().catch(() => {}); // hard auth failure -> drop secure copy
        clearListCache();
        localStorage.removeItem('authToken');
        setTimeout(() => window.location.reload(), 500);
      } else if (
        msg.includes("That's only")
        || msg.includes("Can't place that call")
      ) {
        setStatus(msg);
      } else if (msg.includes('not registered')) {
        // NativeVoice.connect() throws this when the SIP client's internal
        // _registered flag is false at call time — confirmed on device
        // (2026-08-26): after a few outbound calls, this fires with zero
        // server-side trace (connect() throws before ever reaching
        // /api/outbound/start), and every call attempt after keeps failing
        // the exact same way — nothing here previously re-registered, so
        // the device stayed silently broken until the app was relaunched.
        // Bumping deviceConnectGeneration re-runs the device-init effect,
        // which calls register() fresh (native register() already clears
        // stale accounts/authinfo first, so this is safe to call again).
        console.warn('[CALL] SIP client not registered — triggering re-registration');
        setStatus('Phone line disconnected — reconnecting…');
        setActiveCall(null);
        setCallConnected(false);
        setCallStatus('closed');
        setDeviceReady(false);
        stopTimer();
        setDeviceConnectGeneration((n) => n + 1);
      } else {
        setStatus(UNREACHABLE_MESSAGE);
        setActiveCall(null);
        setCallConnected(false);
        setCallStatus('closed');
        stopTimer();
      }
    }
  };

  // ── Hang up ───────────────────────────────────────────
  const handleHangup = () => {
    // TRIPWIRE — handleHangup should only be called by user clicking End.
    // If this log appears WITHOUT the user clicking End, something is
    // calling handleHangup programmatically (which would explain a
    // mysterious SDK leg death).
    console.warn(`[HANGUP-TRIPWIRE] handleHangup invoked at ${new Date().toISOString()} stack:`, new Error('hangup origin').stack);

    // ── BRIDGE GUARD ──
    // If a bridge transition is in progress (isBridgingRef.current === true
    // for the 30 s window after a successful /call/add), REFUSE the
    // hangup. A click that lands during this window is almost always a
    // stale event handler, a parent-component re-mount cascade, or a
    // programmatic call — NOT a deliberate user intent to drop a mid-
    // bridge call. Log the refusal with a stack trace so the caller is
    // identifiable. After the 30 s ceiling expires the lock auto-releases
    // and subsequent hangup clicks proceed normally.
    if (isBridgingRef.current) {
      console.warn(
        `[HANGUP-TRIPWIRE] !!! REFUSED at ${new Date().toISOString()}: isBridging=true ` +
        `(bridge transition in progress; hangup blocked to protect active leg). ` +
        `Click ignored — wait for the 30s handoff window to expire, then click End again. ` +
        `Stack of refused caller:`,
        new Error('refused hangup during bridge').stack
      );
      return;
    }

    // Clear any active handoff grace so the SDK `disconnect` event we are
    // about to trigger (via activeCall.disconnect()) is NOT swallowed by
    // the grace short-circuit in the disconnect handler. The user clicking
    // End is unambiguous intent to tear down — the grace only exists to
    // ride out automatic mid-handoff signaling, not deliberate hangups.
    clearHandoffGrace('user_hangup');
    // Stamp hangup time so the error handler can suppress error 31005
    // (ConnectionError), which fires as a side-effect of WebRTC teardown.
    hangupInitiatedAtRef.current = Date.now();
    const hangupSid =
      activeCall?.parameters?.CallSid || activeCall?.sid || activeCall?.callSid || '';
    if (hangupSid) {
      // Fire-and-forget: cancel REST-dialed PSTN legs still ringing so the
      // callee's phone stops when we hang up before they answer. /call-status
      // also performs this cleanup, but hitting the API here is faster.
      fetch(`${API_BASE}/call/cancel`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ call_sid: hangupSid }),
      }).catch((e) => {
        console.warn('[HANGUP] /call/cancel failed (non-fatal):', e);
      });
    }
    activeCall?.disconnect();
    try { releaseWakeLock(); } catch (e) {}
    setActiveCall(null);
    setCallConnected(false);
    setCallStatus('closed');
    setIsMuted(false);
    setIsSpeaker(false);
    setIsOnHold(false);
    stopTimer();
    setStatus('Call ended');
    clearTransferGate();
  };

  // ── AI-transfer whisper keypress gate ──────────────────
  // An AI-transfer conference is named "Xfer_*". The server plays "Press 1 to
  // accept, 2 to decline" on the answered leg, then waits ~10s for that DTMF,
  // so the accepting staffer needs big touch targets for 1 and 2.
  // clearTransferGate remembers which conf was dismissed, so a late
  // answer-inbound response cannot re-arm the banner after a keypress.
  const clearTransferGate = useCallback(() => {
    if (transferGateTimerRef.current) {
      clearTimeout(transferGateTimerRef.current);
      transferGateTimerRef.current = null;
    }
    setTransferGate((cur) => {
      if (cur?.conf) transferGateDismissedRef.current = cur.conf;
      return null;
    });
  }, []);

  const armTransferGate = useCallback((confName) => {
    const conf = String(confName || '');
    if (!/^Xfer_/i.test(conf)) return;
    if (transferGateDismissedRef.current === conf) return;
    if (transferGateTimerRef.current) clearTimeout(transferGateTimerRef.current);
    setTransferGate({ conf });
    // Safety ceiling — the server's whisper window is ~10s; never leave the
    // banner (or its status override) up longer than a call could need it.
    transferGateTimerRef.current = setTimeout(() => {
      transferGateTimerRef.current = null;
      transferGateDismissedRef.current = conf;
      setTransferGate(null);
    }, 25000);
  }, []);

  // Any teardown path (disconnect, error, answered-elsewhere) clears the gate.
  useEffect(() => {
    if (!activeCall && transferGate) clearTransferGate();
  }, [activeCall, transferGate, clearTransferGate]);

  useEffect(() => () => {
    if (transferGateTimerRef.current) clearTimeout(transferGateTimerRef.current);
  }, []);

  const handleTransferKey = (digit) => {
    if (!activeCall) return;
    try {
      activeCall.sendDigits(digit);
    } catch (e) {
      console.warn('[XFER-GATE] sendDigits failed:', e?.message || e);
      return;
    }
    setDtmfEcho((prev) => (prev + digit).slice(-24));
    clearTransferGate();
    // 2 = decline: drop our leg through the normal hangup path. 1 = accept, and
    // the call just continues (status falls back to "Connected" ungated).
    if (digit === '2') handleHangup();
  };

  // ── Mute toggle ──────────────────────────────────────
  const handleMuteToggle = () => {
    if (!activeCall) return;
    if (isOnHold) { setStatus('Mute is disabled while the call is on hold'); return; }
    const next = !isMuted;
    try {
      activeCall.mute(next);
      setIsMuted(next);
    } catch (e) {
      console.error('[MUTE] toggle failed:', e);
      setStatus('Mute toggle failed');
    }
  };

  // ── Speaker / Earpiece toggle ─────────────────────────
  // Shared by handleSpeakerToggle and handleDeviceCycle. For a native SIP
  // call, route through NativeVoice (LinphoneManager) rather than the
  // WebView-era AudioRoutePlugin — see the comment in handleSpeakerToggle
  // for why running both against the same call can leave the OS route and
  // linphone's own core.outputAudioDevice disagreeing about what's active.
  const setSpeakerRoute = async (enabled) => {
    if (isNativeVoiceEnabled() && NativeVoice && NativeVoice.setSpeaker) {
      return NativeVoice.setSpeaker({ enabled });
    }
    return AudioRoute?.setSpeaker({ enabled });
  };

  const handleSpeakerToggle = async () => {
    if (!activeCall) {
      console.warn('[AUDIO] No active call, ignoring speaker toggle');
      return;
    }
    const next = !isSpeaker;
    try {
      await setSpeakerRoute(next);
      // speaker toggle clears bluetooth state
      if (next) setIsBluetooth(false);
      setIsSpeaker(next);
      console.log('[AUDIO] Speaker set to:', next);

      // Read the route back from AVAudioSession and surface it on screen.
      // getSpeaker() reports what iOS ACTUALLY did, which can differ from what
      // we asked for if WebKit re-asserts its own route. Showing it in the
      // status line means route problems are diagnosable on the device itself,
      // without needing Safari's Web Inspector attached.
      //
      // This readback exists to catch WKWebView's WebRTC audio unit fighting
      // back on the legacy web-calling path (see AudioRoutePlugin.swift) —
      // AudioRoute.getSpeaker() and NativeVoice's route change both ultimately
      // read/write the same AVAudioSession, but LinphoneManager's device
      // switch (used for native calls) can take a beat to land, and reading
      // it back immediately here would then "correct" a still-in-flight
      // change right back to Earpiece — the exact "no way to enable speaker"
      // symptom this was silently causing. Only let the readback override
      // the optimistic state on the WebView path, where the race it exists
      // to catch actually happens.
      if (AudioRoute && AudioRoute.getSpeaker) {
        const actual = await AudioRoute.getSpeaker().catch(() => null);
        if (actual && typeof actual.speaker === 'boolean') {
          const asked = next ? 'Speaker' : 'Earpiece';
          const got = actual.speaker ? 'Speaker' : 'Earpiece';
          setStatus(asked === got ? `Audio: ${got}` : `Audio: asked ${asked}, got ${got}`);
          if (!isNativeVoiceEnabled() && actual.speaker !== next) setIsSpeaker(actual.speaker);
          console.log('[AUDIO] requested:', asked, 'actual:', got);
        }
      }
    } catch (err) {
      console.error('[AUDIO] Speaker toggle failed:', err);
      setStatus(`Audio route failed: ${err?.message || err}`);
    }
  };

  const handleDeviceCycle = async () => {
    // Cycle: earpiece -> speaker -> bluetooth -> earpiece
    if (!activeCall) return;
    try {
      // Speaker/earpiece goes through setSpeakerRoute (native-aware — see
      // handleSpeakerToggle). Bluetooth selection has no native-path
      // equivalent yet (LinphoneManager doesn't expose one), so that part
      // still goes through the WebView-era AudioRoutePlugin regardless of
      // which call path is active — a real gap for native calls, not
      // something to silently paper over here.
      if (!isSpeaker && !isBluetooth) {
        // earpiece -> speaker
        await setSpeakerRoute(true);
        setIsSpeaker(true);
        setIsBluetooth(false);
      } else if (isSpeaker && !isBluetooth) {
        // speaker -> bluetooth if one is connected, else back to earpiece.
        // The AVAudioSession is configured with .allowBluetooth /
        // .allowBluetoothA2DP, so iOS routes to a paired device on its own
        // once the speaker override is cleared.
        const bt = await AudioRoute?.selectBluetooth?.().catch(() => null);
        if (bt?.bluetooth) {
          setIsBluetooth(true);
          setIsSpeaker(false);
        } else {
          await setSpeakerRoute(false);
          setIsSpeaker(false);
          setIsBluetooth(false);
        }
      } else {
        // bluetooth or other -> earpiece
        await setSpeakerRoute(false);
        setIsSpeaker(false);
        setIsBluetooth(false);
      }
    } catch (err) {
      console.error('[AUDIO] device cycle failed', err);
    }
  };

  // System call waiting (Android Telecom): when another call (cellular, WhatsApp, Ooma) is
  // answered, Android asks us to hold this call and to resume it afterwards. Hold/resume through
  // the same server endpoint as the Hold button. This never hangs the call up.
  const authTokenRef = useRef(authToken);
  authTokenRef.current = authToken;
  const systemHeldRef = useRef(false);
  useEffect(() => {
    if (!isNativePlatform || (Capacitor.getPlatform?.() || '') !== 'android') return undefined;
    if (typeof AudioRoute?.addListener !== 'function') return undefined;
    const setHold = async (action) => {
      const call = activeCallRef.current;
      const sid = call?.parameters?.CallSid || call?.sid || call?.callSid;
      if (!sid) return;
      if (action === 'resume' && !systemHeldRef.current) return; // only undo a hold we made
      try {
        const res = await fetch(`${API_BASE}/call/hold`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authTokenRef.current}`,
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
            'Idempotency-Key': newIdempotencyKey(),
          },
          body: JSON.stringify({ call_sid: sid, action }),
        });
        if (!res.ok) { console.warn('[TELECOM] system', action, 'failed:', res.status); return; }
        systemHeldRef.current = action === 'hold';
        setIsOnHold(action === 'hold');
        setStatus(action === 'hold' ? 'On hold' : 'Resumed');
      } catch (e) {
        console.warn('[TELECOM] system', action, 'error:', e?.message || e);
      }
    };
    const handles = [
      AudioRoute.addListener('telecomHold', () => { setHold('hold'); }),
      AudioRoute.addListener('telecomUnhold', () => { setHold('resume'); }),
    ];
    return () => {
      handles.forEach((p) => { Promise.resolve(p).then((h) => h?.remove?.()).catch(() => {}); });
    };
  }, []);
  useEffect(() => { if (!activeCall) systemHeldRef.current = false; }, [activeCall]);

  const handleHoldToggle = async () => {
    if (!activeCall) return;
    if (!callConnected) {
      setStatus('Hold available after the call connects');
      return;
    }
    // Ensure AudioContext resumed immediately on user interaction
    try {
      if (window.__truvaAudioContext && window.__truvaAudioContext.state === 'suspended') {
        await window.__truvaAudioContext.resume();
        console.log('[AUDIO] window.__truvaAudioContext.resume() called from handleHoldToggle');
      }
      if (deviceRef.current && deviceRef.current.audio && deviceRef.current.audio.audioContext && typeof deviceRef.current.audio.audioContext.resume === 'function') {
        try { await deviceRef.current.audio.audioContext.resume(); console.log('[AUDIO] deviceRef.current.audio.audioContext.resume()'); } catch (e) { console.warn('[AUDIO] device audioContext resume failed', e); }
      }
    } catch (e) {
      console.warn('[AUDIO] pre-hold audio resume failed', e);
    }

    const sid = activeCall?.parameters?.CallSid || activeCall?.sid || activeCall?.callSid || activeCall?.parameters?.CallSid;
    if (!sid) {
      setStatus('No Call SID available to hold');
      return;
    }
    const action = isOnHold ? 'resume' : 'hold';
    if (isHoldPending) {
      console.log('[HOLD] hold/resume already pending; ignoring duplicate click');
      return;
    }
    setIsHoldPending(true);
    try {
      const fullUrlHold = `${API_BASE}/call/hold`;
      const idempotencyKey = newIdempotencyKey();
      console.log('[FETCH]', fullUrlHold, 'payload=', { call_sid: sid, action }, 'key=', idempotencyKey);
      const res = await fetch(fullUrlHold, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({ call_sid: sid, action }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setStatus('Hold failed: ' + (data.error || res.status));
        setIsHoldPending(false);
        return;
      }
      // toggle UI state only after successful server response
      setIsOnHold(!isOnHold);
      setStatus(isOnHold ? 'Resumed' : 'On hold');
      setIsHoldPending(false);
    } catch (e) {
      console.error('[HOLD] request failed', e);
      setStatus('Hold request failed');
      setIsHoldPending(false);
    }
  };

  // On-demand recording (Record/Stop). The backend is the source of truth
  // for the recording bool and enforces mode + entitlement + ownership.
  const recCallSid = () =>
    activeCall?.parameters?.CallSid || activeCall?.sid || activeCall?.callSid || activeCall?.parameters?.CallSid;
  const handleRecordToggle = async (wantRecording) => {
    const sid = recCallSid();
    if (!sid) { setStatus('No Call SID available to record'); return; }
    if (recordingBusy) return;
    setRecordingBusy(true);
    try {
      const ep = wantRecording
        ? `${API_BASE}/api/calls/${encodeURIComponent(sid)}/recording/start`
        : `${API_BASE}/api/calls/${encodeURIComponent(sid)}/recording/stop`;
      const res = await fetch(ep, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ call_sid: sid }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403 || res.status === 409) setRecordingSupported(false);
        setStatus('Recording unavailable: ' + (data.error || res.status));
        return;
      }
      const onNow = !!data.recording;
      setIsRecording(onNow);
      if (onNow) setRecordingSeconds(0);
      setStatus(onNow ? 'Recording' : 'Recording stopped');
    } finally {
      setRecordingBusy(false);
    }
  };
  // Red-dot timer while recording.
  useEffect(() => {
    if (!isRecording) return;
    const iv = setInterval(() => setRecordingSeconds((sec) => sec + 1), 1000);
    return () => clearInterval(iv);
  }, [isRecording]);
  // Reset recording UI when the active call closes.
  useEffect(() => {
    if (!activeCall) { setIsRecording(false); setRecordingSeconds(0); setRecordingSupported(true); }
  }, [activeCall]);

  // CallControls' Keypad button onClick — toggles the active-call view
  // to the Dialpad (for DTMF input). Pair with the "Back to Call" button
  // rendered in the dialpad footer to switch back to CallControls.
  const handleShowDialpad = () => {
    setDtmfEcho('');
    setShowDialpad(true);
  };

  const handleHideDialpad = () => {
    setShowDialpad(false);
    setDialpadTarget(null);
    setDialpadNumber('');
    setDtmfEcho('');
  };

  // Reset to CallControls view whenever a call ends. One useEffect with
  // [activeCall] dep covers all teardown paths (disconnect, error,
  // user-hangup, etc.) without patching every site that calls
  // setActiveCall(null). The next call lands on CallControls by default.
  useEffect(() => {
    if (!activeCall && showDialpad) {
      setShowDialpad(false);
    }
    if (!activeCall && dialpadTarget) {
      setDialpadTarget(null);
      setDialpadNumber('');
    }
    if (!activeCall && dtmfEcho) {
      setDtmfEcho('');
    }
    if (!activeCall && (isTranslating || showTranslate)) {
      setIsTranslating(false);
      setShowTranslate(false);
    }
  }, [activeCall, showDialpad, dialpadTarget, dtmfEcho, isTranslating, showTranslate]);

  // When a translated call ends (`activeCall` clears), offer once to remember
  // the language it used for that number -- never mid-call. The classifier
  // decides save/always/keep (and returns null when there's nothing to offer);
  // per-number 30-day suppression lives in lib/translateRecall.
  useEffect(() => {
    if (activeCall) return;
    const info = lastTranslateRef.current;
    if (!info) return;
    lastTranslateRef.current = null;
    if (shouldSuppressRecall(info.number)) return;
    setRecallPrompt(buildTranslateRecall(info.number, info.lang, savedContacts));
  }, [activeCall]);

  const handleAddCall = async (targetArg) => {
    if (!activeCall) {
      setStatus('No active call to add');
      return;
    }
    if (!callConnected) {
      setStatus('Add available after the call connects');
      return;
    }
    // Stamp the recent-backend-op marker AT THE BUTTON-CLICK MOMENT, not
    // later in the flow. This covers the whole user-interaction window —
    // from clicking Add through any prompt() blocking, through the fetch
    // itself, and the subsequent SDK signaling window where a transient
    // `disconnect` event might fire.
    recentBackendOpRef.current = Date.now();
    const sid = activeCall?.parameters?.CallSid || activeCall?.sid || activeCall?.callSid || '';
    const target = targetArg || window.prompt('Enter number to add to call (e.g. +13105551212):');
    if (!target) {
      setStatus('Add cancelled');
      return;
    }

    // ── HANDOFF TIMER + GUARD START — synchronized to OK-click ──
    // This is the exact moment of user intent (post-typing, post-OK).
    // Both the latency measurement (handoffClickTimeRef) AND the safety
    // lock (armHandoffGrace) start here, so:
    //   • the disconnect handlers can report true OK-click → disconnect
    //     latency (not polluted by fetch RTT)
    //   • the 30 s sticky lock covers the WHOLE backend round-trip plus
    //     the redirect + ring window, not just the post-fetch tail
    handoffClickTimeRef.current = Date.now();
    armHandoffGrace();
    console.log(
      `[CALL ADD] OK click captured at ${new Date(handoffClickTimeRef.current).toISOString()} — ` +
      `latency timer + handoff lock armed in lock-step`
    );

    try {
      setStatus('Sending add request...');
      const fullUrlAdd = `${API_BASE}/call/add`;
      const idempotencyKey = newIdempotencyKey();
      // Re-stamp right before the fetch as a defensive top-up. The
      // primary stamp at button-click handles the prompt() blocking;
      // this top-up resets the window once the network call goes out.
      // so the disconnect-guard window covers any SDK signaling event
      // triggered by the request landing on the backend.
      recentBackendOpRef.current = Date.now();
      console.log('[FETCH]', fullUrlAdd, 'payload=', { call_sid: sid, target, from: selectedNumber }, 'key=', idempotencyKey);
      const res = await fetch(fullUrlAdd, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({ call_sid: sid, target, from: selectedNumber }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        // Do NOT clearHandoffGrace here. If a prior Add's lock is still
        // active, its handoff is still in progress and must hold for
        // the full 30 s window. This failed Add doesn't change that.
        // The lock is released ONLY by the setTimeout or by the user
        // clicking End (handleHangup).
        setStatus('Add failed: ' + (data.error || res.status));
        return;
      }
      // (Handoff grace was already armed at the OK-click moment above,
      // synchronized with handoffClickTimeRef so the latency measurement
      // and the safety lock start at the same instant.)
      setStatus('Add request sent');

      // Refresh roster for the current call so UI shows pending/added participants
      try {
        await fetchParticipants(sid);
      } catch (e) {
        console.warn('[CALL ADD] refresh roster failed', e);
      }

    } catch (e) {
      // Do NOT clearHandoffGrace here — same reasoning as the !res.ok
      // branch above: a failed Add must not snipe a prior Add's still-
      // active 30 s sticky lock. Release is reserved for setTimeout or
      // user-hangup.
      console.error('[CALL ADD] error', e);
      setStatus('Add request failed');
    }
  };

  const handleTransfer = async (targetArg) => {
    if (!activeCall) {
      setStatus('No active call to transfer');
      return;
    }
    if (!callConnected) {
      setStatus('Transfer available after the call connects');
      return;
    }
    // Stamp at button-click — see handleAddCall for rationale.
    recentBackendOpRef.current = Date.now();
    const sid = activeCall?.parameters?.CallSid || activeCall?.sid || activeCall?.callSid || '';
    const target = targetArg || window.prompt('Enter number to transfer call to (e.g. +13105551212):');
    if (!target) {
      setStatus('Transfer cancelled');
      return;
    }
    try {
      setStatus('Sending transfer request...');
      const fullUrlTransfer = `${API_BASE}/call/transfer`;
      const idempotencyKey = newIdempotencyKey();
      // See handleAddCall for the rationale on recentBackendOpRef.
      recentBackendOpRef.current = Date.now();
      console.log('[FETCH]', fullUrlTransfer, 'payload=', { call_sid: sid, target, from: selectedNumber }, 'key=', idempotencyKey);
      const res = await fetch(fullUrlTransfer, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({ call_sid: sid, target, from: selectedNumber }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        setStatus('Transfer failed: ' + (data.error || res.status));
        return;
      }
      setStatus('Transfer request sent');
    } catch (e) {
      console.error('[CALL TRANSFER] error', e);
      setStatus('Transfer request failed');
    }
  };

  // Add/Transfer buttons in CallControls open the mid-call Dialpad in
  // number-entry mode (see dialpadTarget) instead of calling
  // handleAddCall/handleTransfer directly — those still run the actual
  // request once a number is entered and the dialpad's Call button is
  // pressed, via handleDialpadEntrySubmit below.
  const handleAddClick = () => {
    if (!activeCall) {
      setStatus('No active call to add');
      return;
    }
    if (!callConnected) {
      setStatus('Add available after the call connects');
      return;
    }
    setDialpadNumber('');
    setDialpadTarget('add');
    setShowDialpad(true);
  };

  const handleTransferClick = () => {
    if (!activeCall) {
      setStatus('No active call to transfer');
      return;
    }
    if (!callConnected) {
      setStatus('Transfer available after the call connects');
      return;
    }
    setDialpadNumber('');
    setDialpadTarget('transfer');
    setShowDialpad(true);
  };

  const handleDialpadEntrySubmit = async () => {
    const target = dialpadNumber.trim();
    if (!target) return;
    const checked = validateUsDialNumber(target);
    if (!checked.ok) {
      setStatus(checked.error);
      return;
    }
    const purpose = dialpadTarget;
    setDialpadTarget(null);
    setDialpadNumber('');
    setShowDialpad(false);
    if (purpose === 'add') {
      await handleAddCall(checked.e164);
    } else if (purpose === 'transfer') {
      await handleTransfer(checked.e164);
    }
  };

  // ── Hold / Resume ──────────────────────────────────────
  // Named alias consumed by CallControls' onHold prop. Delegates to the
  // full handleHoldToggle which handles AudioContext, pending-guard, etc.
  const handleHold = handleHoldToggle;

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    callConnectedRef.current = callConnected;
  }, [callConnected]);

  // ── Accept incoming call ─────────────────────────────
  const handleAccept = async () => {
    if (!incomingCall) return;
    stopRingtone(ringtoneRef.current);
    ringtoneRef.current = null;
    vibrate(0);
    console.log('[INCOMING] Accepting call...');

    if (incomingCall._softphoneInbound) {
      inboundDismissingRef.current = true;
      if (inboundExpireTimerRef.current) {
        clearTimeout(inboundExpireTimerRef.current);
        inboundExpireTimerRef.current = null;
      }
      setCallStatus('connecting');
      const fromHint = incomingCall.parameters?.From;
      const dialHint = incomingCall._inboundMeta?.dial_address || '';
      const pstnHint = incomingCall.parameters?.CallSid || '';
      const wakeToken = incomingCall._inboundMeta?.wake_token || '';
      // AI-transfer rings carry their conference name at ring time; "Xfer_*"
      // means the server whispers "press 1 / 2" on the leg we join.
      const confHint = incomingCall._inboundMeta?.conf_name || '';
      const prejoinPromise = incomingCall._prejoinPromise;
      setIncomingCall(null);
      setStatus('Connecting…');
      const wireTeardown = (call) => {
        const teardown = () => {
          try { releaseWakeLock(); } catch (e) {}
          setActiveCall(null);
          setCallConnected(false);
          setCallStatus('closed');
          setIsMuted(false);
          setIsOnHold(false);
          setIsSpeaker(false);
          stopTimer();
          setStatus('Call ended');
          clearTransferGate();
        };
        call.on('disconnect', teardown);
        call.on('graceDisconnect', () => {
          try { call.finishConfirmed?.(); } catch (_) { teardown(); }
        });
      };
      // Another user answered first (server replied 409 already_answered):
      // drop our leg and say why instead of leaving a dead "Connected" call.
      const isAnsweredElsewhere = (res) => res && res.ok === false && res.body && res.body.error === 'already_answered';
      const answeredElsewhere = (call) => {
        try { call.disconnect(); } catch (e) {}
        try { releaseWakeLock(); } catch (e) {}
        setActiveCall(null);
        setCallConnected(false);
        setCallStatus('closed');
        setIsMuted(false);
        setIsOnHold(false);
        setIsSpeaker(false);
        stopTimer();
        setStatus('Answered on another device');
        clearTransferGate();
      };
      try {
        // Finish mic warm / join reserve before answer-inbound pops the ring.
        const reserved = prejoinPromise ? await prejoinPromise : null;
        if (reserved?.call) {
          // Legacy path (already dialed muted during ring) — unmute only.
          const call = reserved.call;
          try { call.mute(false); } catch (_) {}
          setActiveCall(call);
          setCallConnected(true);
          setCallStatus('open');
          setStatus('Connected');
          try { requestWakeLock(); } catch (e) {}
          startTimer();
          wireTeardown(call);
          armTransferGate(reserved?.body?.conf_name || confHint);
          fetch(`${API_BASE}/api/softphone/answer-inbound`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
            },
            body: JSON.stringify({ wake_token: wakeToken }),
          }).then((res) => res.json().catch(() => ({})).then((body) => ({ ok: res.ok, body })))
            .then((res) => {
              if (isAnsweredElsewhere(res)) { answeredElsewhere(call); return; }
              armTransferGate(res.body?.conf_name || '');
            })
            .catch((e) => console.warn('[INCOMING] answer-inbound:', e?.message || e));
          return;
        }

        // answer-inbound is server-side signaling only (marks accepted,
        // clears the caller's hold music, cancels sibling SIP rings) — it
        // does not gate the client's own WebRTC dial. Every value
        // connectInboundJoin needs already has a ring-time fallback
        // (dialHint/fromHint/pstnHint), so start the dial immediately in
        // parallel instead of waiting on this round-trip first — that was
        // adding its full network latency to the front of the ~5s cold
        // WebRTC connect time before any negotiation even began.
        const dialAddress = reserved?.body?.dial_address || dialHint;
        if (!dialAddress) throw new Error('missing dial_address');
        const answerPromise = fetch(`${API_BASE}/api/softphone/answer-inbound`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({ wake_token: wakeToken }),
        }).then((res) => res.json().catch(() => ({})).then((body) => ({ ok: res.ok, body })))
          .catch((e) => {
            console.warn('[INCOMING] answer-inbound:', e?.message || e);
            return { ok: false, body: {} };
          });

        // Cold start: the pending-call pull answers before the voice device has registered, so the
        // device may not exist yet. Wait for it (bounded) instead of failing with a null deref.
        for (let i = 0; i < 100 && !deviceRef.current; i += 1) {
          await new Promise((r) => setTimeout(r, 150));
        }
        if (!deviceRef.current) throw new Error('voice device not ready');
        const call = await deviceRef.current.connectInboundJoin(dialAddress, {
          from: fromHint,
          callSid: pstnHint || '',
        });

        answerPromise.then((res) => {
          if (isAnsweredElsewhere(res)) { answeredElsewhere(call); return; }
          if (!res.ok) console.warn('[INCOMING] answer-inbound failed:', res.body?.error || 'unknown error');
          armTransferGate(res.body?.conf_name || '');
        });
        setActiveCall(call);
        setCallConnected(true);
        setCallStatus('open');
        setStatus('Connected');
        try { requestWakeLock(); } catch (e) {}
        startTimer();
        wireTeardown(call);
        armTransferGate(confHint);
      } catch (e) {
        console.error('[INCOMING] softphone answer failed:', e);
        setStatus('Answer failed: ' + (e.message || e));
        setCallStatus('closed');
      } finally {
        inboundDismissingRef.current = false;
      }
      return;
    }

    // Register accept BEFORE calling .accept() so we don't miss the event.
    // Hold/Transfer/Add stay disabled until this fires.
    incomingCall.on('accept', () => {
      console.log('[INCOMING] accept event fired; controls unlocked. status=', incomingCall.status?.());
      setCallConnected(true);
      setCallStatus('open');
      setStatus('Connected');
      try { requestWakeLock(); } catch (e) {}
      startTimer();
    });

    // Hold/unhold (second-call interruption / call waiting) on the inbound leg.
    incomingCall.on('hold', () => {
      console.log('[INCOMING] On hold (interruption)');
      setIsOnHold(true);
      setStatus('On hold');
    });
    incomingCall.on('unhold', () => {
      console.log('[INCOMING] Resumed');
      setIsOnHold(false);
      setStatus('Connected');
    });

    incomingCall.accept();
    setActiveCall(incomingCall);
    setCallStatus('connecting');
    setIncomingCall(null);
    setStatus('Connecting...');

    incomingCall.on('disconnect', () => {
      try { releaseWakeLock(); } catch (e) {}
      setActiveCall(null);
      setCallConnected(false);
      setCallStatus('closed');
      setIsMuted(false);
      setIsOnHold(false);
      setIsSpeaker(false);
      stopTimer();
      setStatus('Call ended');
    });
  };

  // ── Reject incoming call ─────────────────────────────
  const handleReject = () => {
    stopRingtone(ringtoneRef.current);
    ringtoneRef.current = null;
    vibrate(0);
    if (incomingCall?._softphoneInbound && authToken) {
      inboundDismissingRef.current = true;
      if (inboundExpireTimerRef.current) {
        clearTimeout(inboundExpireTimerRef.current);
        inboundExpireTimerRef.current = null;
      }
      const wakeToken = incomingCall._inboundMeta?.wake_token || '';
      const prejoinPromise = incomingCall._prejoinPromise;
      if (prejoinPromise) {
        prejoinPromise.then((pre) => {
          try { pre?.call?.disconnect?.(); } catch (_) {}
        }).catch(() => {});
      }
      // Clear UI only after reject lands so the pending poll cannot re-show
      // the same ring (fire-and-forget used to race and re-trigger).
      const dying = incomingCall;
      releaseWarmMic();
      setIncomingCall(null);
      setStatus('Call declined');
      endCallKitCall();
      fetch(`${API_BASE}/api/softphone/reject-inbound`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ wake_token: wakeToken }),
      })
        .catch(() => {})
        .finally(() => {
          // Keep the guard briefly so a late poll can't resurrect this ring.
          setTimeout(() => {
            if (incomingCallRef.current === dying) return;
            inboundDismissingRef.current = false;
          }, 1500);
        });
      return;
    }
    incomingCall?.reject();
    setIncomingCall(null);
    setStatus('Call declined');
    endCallKitCall();
  };

  handleAcceptRef.current = handleAccept;
  handleRejectRef.current = handleReject;

  // ── Backspace on number input ─────────────────────────
  const handleBackspace = () => {
    setDialNumber((prev) => String(prev || '').slice(0, -1));
  };

  // ── Render ───────────────────────────────────────────
  if (!authToken || !user) {
    // A native-SIP call can be answered entirely outside the web session
    // (VoIP push → CallKit → Linphone, see CallKitManager.swift) — CallKit's
    // own system call UI already owns the actual call controls here, so
    // this just needs to avoid stomping an in-progress call with the sign-in
    // screen, not reimplement in-call controls.
    if (nativeCallActive) {
      return (
        <div className="login-screen">
          <div className="login-card">
            <div className="login-header">
              <h1>Truva</h1>
              <p>On a call</p>
            </div>
          </div>
        </div>
      );
    }
    // Sign-in is Solvit Hub SSO only (see /auth/sso/login).
    return <Login />;
  }

  // Still waiting on the native hasToken() check (see the tri-state comment
  // on bioGate above) -- render neither the real app nor the unlock screen
  // until we actually know which one is correct.
  if (bioGate === null && !nativeCallActive) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="login-header">
            <h1>Truva</h1>
          </div>
        </div>
      </div>
    );
  }

  // Biometric cold-launch gate: only when a session already exists but the
  // OS holds a biometric-protected copy. Never over an in-progress/native call.
  if (bioGate && authToken && user && !nativeCallActive) {
    return (
      <BiometricUnlock
        onUnlocked={(tok) => {
          setAuthToken(tok);
          setBioGate(false);
        }}
        onUsePassword={() => {
          // Fall back to the normal sign-in (SSO or password). Clear both the
          // plaintext session and the biometric-gated copy so the app returns
          // to the login screen (the required password-login fallback path).
          BiometricSecure.clearToken().catch(() => {});
          clearListCache();
          localStorage.removeItem('authToken');
          localStorage.removeItem('truva_session');
          localStorage.removeItem('user');
          setAuthToken('');
          setUser(null);
          setBioGate(false);
        }}
      />
    );
  }

  return (
    <AuthProvider>
    <div className={`softphone${narrowLayout ? ' softphone--narrow' : ''}`} ref={softphoneRef}>
      {/* Incoming call top banner. Android's OS notification (see
          InboundRingService.java) only heads-up-displays while the app is
          NOT in the foreground — the two are naturally mutually exclusive,
          so no suppression is needed here; whenever this can render, the
          OS banner isn't showing anyway. */}
      <IncomingCall
        callerInfo={
          incomingCall
            ? sanitizeCallerDisplay(incomingCall.parameters?.From)
            : null
        }
        callerName={
          incomingCall
            ? incomingCallerName(
                sanitizeCallerDisplay(incomingCall.parameters?.From),
                incomingLineContacts,
                savedContacts,
              )
            : null
        }
        lineInfo={
          incomingCall
            ? permittedIncomingLineLabel(
                incomingCall._inboundMeta?.to
                  || incomingCall.parameters?.To
                  || '',
                phoneNumbers,
              )
            : null
        }
        onAccept={handleAccept}
        onReject={handleReject}
      />

      {/* Header */}
      <header className="sp-header">
        <h1>Truva</h1>
        <div className="header-right">
          {phoneNumbers.length > 0 && (
            <label className="line-picker" title="Outbound calls and SMS use this line">
              <span className="line-picker-label">Line</span>
              <select
                className="number-selector"
                value={selectedNumber}
                aria-label="Active line for calls and SMS"
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedNumber(v);
                  saveSelectedLineForUser(user, v);
                }}
              >
                {phoneNumbers.length > 1 && (
                  <option value="">Select line…</option>
                )}
                {phoneNumbers.map((n) => (
                  <option key={n.phone_number} value={n.phone_number}>
                    {formatCallerIdOption(n)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <span
            /* Purely presentational modifier so the CSS can paint the dot amber
               while connecting; the existing online/offline class is untouched. */
            className={`status-badge ${deviceReady ? 'online' : 'offline'}${!deviceReady && deviceInitRequested ? ' connecting' : ''}`}
            data-testid="truva-device-status"
          >
            {deviceReady ? '● Online' : (deviceInitRequested ? '… Connecting' : '○ Offline')}
          </span>
          {/* Connect button is hidden in the happy path — auto-init useEffect arms
              deviceInitRequested as soon as authToken is present. Surface a
              "Retry Connect" button only if init is taking unusually long
              (deviceInitRequested true for ≥5s but deviceReady still false). */}
          {!deviceReady && deviceInitRequested && (
            <button
              className="connect-btn btn-primary"
              onClick={handleConnectClick}
              style={{ marginLeft: 8 }}
              title="Retry connecting to SignalWire Voice"
            >
              Retry Connect
            </button>
          )}
          {deviceReady && typeof window !== 'undefined' && (window.__truvaAudioContext && window.__truvaAudioContext.state === 'suspended') && (
            <button
              className="unlock-audio btn-primary"
              onClick={async () => {
                try {
                  if (window.__truvaAudioContext && window.__truvaAudioContext.state === 'suspended') {
                    await window.__truvaAudioContext.resume();
                    console.log('[AUDIO] Unlock Audio button resumed window.__truvaAudioContext');
                  }
                  if (deviceRef.current && deviceRef.current.audio && deviceRef.current.audio.audioContext && typeof deviceRef.current.audio.audioContext.resume === 'function') {
                    try {
                      await deviceRef.current.audio.audioContext.resume();
                      console.log('[AUDIO] Unlock Audio button resumed device audioContext');
                    } catch (e) {
                      console.warn('[AUDIO] device audioContext resume failed', e);
                    }
                  }
                } catch (e) {
                  console.warn('[AUDIO] Unlock Audio button failed', e);
                }
              }}
              style={{ marginLeft: 8 }}
            >
              Unlock Audio
            </button>
          )}
          <button
            type="button"
            className={`header-settings-btn${tab === 'settings' ? ' active' : ''}`}
            aria-label="Settings"
            title="Settings"
            onClick={() => {
              setMoreTabsOpen(false);
              setAccountMenuOpen(false);
              setTab('settings');
            }}
          >
            <GearIcon filled={tab === 'settings'} size={18} />
          </button>
          <div className="account-menu" ref={accountMenuRef}>
            {narrowLayout ? (
              <button
                type="button"
                className={`account-menu-trigger${accountMenuOpen ? ' open' : ''}`}
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
                aria-label="Account menu"
                title={user?.email || user?.name || 'Account'}
                onClick={() => {
                  setMoreTabsOpen(false);
                  setAccountMenuOpen((o) => !o);
                }}
              >
                {/* Drawn, not the U+22EF glyph: some iPad fonts lack it and the button rendered blank. */}
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
                  <circle cx="3" cy="9" r="1.8" fill="currentColor" />
                  <circle cx="9" cy="9" r="1.8" fill="currentColor" />
                  <circle cx="15" cy="9" r="1.8" fill="currentColor" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                className={`logout-btn account-menu-trigger${accountMenuOpen ? ' open' : ''}`}
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
                title="Account"
                onClick={() => {
                  setMoreTabsOpen(false);
                  setAccountMenuOpen((o) => !o);
                }}
              >
                <span className="account-email">{user?.email || user?.name || 'Account'}</span>
                <span className="account-caret" aria-hidden="true">▾</span>
              </button>
            )}
            {accountMenuOpen && (
              <div className="overflow-menu" role="menu">
                <div className="overflow-menu-email" title={user?.email || user?.name || ''}>
                  {user?.email || user?.name || 'Signed in'}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className="overflow-menu-item danger"
                  onClick={() => {
                    setAccountMenuOpen(false);
                    handleLogout();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Tab content */}
      <main className={`tab-content${tab === 'phone' ? ' tab-content--phone' : ''}`}>
        {/* ─── Phone tab ─── */}
        {tab === 'phone' && (
          <ErrorBoundary inline label="Keypad">
          <div className="main-view-wrapper">
            <div className="phone-tab">
              {/* Number display — hidden during an active call to focus the
                  user on the call controls (Mute / Hold / Keypad / Add /
                  Transfer / End). The Keypad button toggles to the Dialpad
                  for DTMF input mid-call, where the number-entry input
                  isn't useful anyway. */}
              {!activeCall && (
                <div className="number-display truva-glass-secondary" style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="tel"
                    value={number}
                    onChange={(e) => setDialNumber(e.target.value)}
                    placeholder="Enter a number"
                    style={{ flex: 1 }}
                  />
                  <ContactPicker
                    selectedNumber={selectedNumber}
                    authToken={authToken}
                    onSelect={(num) => setDialNumber(num)}
                  />
                </div>
              )}

              {!activeCall && showLiveTranslate && (
                <LiveTranslate
                  ref={liveTranslateRef}
                  authToken={authToken}
                  apiBase={API_BASE}
                  selectedNumber={selectedNumber}
                  initialAction={liveTranslateInitialAction}
                  onActiveChange={setLiveTranslateActive}
                  onOpenSettings={() => {
                    setShowLiveTranslate(false);
                    setLiveTranslateInitialAction(null);
                    setTab('settings');
                  }}
                  onClose={() => {
                    setShowLiveTranslate(false);
                    setLiveTranslateInitialAction(null);
                  }}
                />
              )}

              {/* Status */}
              <div className="call-status" data-testid="truva-status-label">
                {isOnHold && activeCall ? <span className="hold-pill" title="Your call is on hold. Tap Hold/Resume to bring it back.">On hold</span> : null}
                {transferGate && activeCall ? 'Waiting for your keypress: 1 accept, 2 decline' : status}
                {activeCall && <span className="timer"> {formatTime(callTimer)}</span>}
              </div>

              {/* AI-transfer whisper gate: the server played "Press 1 to accept,
                  2 to decline" on this leg and is waiting ~10s for the DTMF, so
                  the two keys get big buttons here instead of the dialpad. */}
              {activeCall && transferGate && (
                <div className="xfer-gate" data-testid="xfer-gate">
                  <div className="xfer-gate-title">AI transfer — press 1 to accept, 2 to decline</div>
                  <div className="xfer-gate-actions">
                    <button
                      type="button"
                      className="btn-accept xfer-gate-btn"
                      data-testid="xfer-accept"
                      onClick={() => handleTransferKey('1')}
                    >
                      Accept (1)
                    </button>
                    <button
                      type="button"
                      className="btn-reject xfer-gate-btn"
                      data-testid="xfer-decline"
                      onClick={() => handleTransferKey('2')}
                    >
                      Decline (2)
                    </button>
                  </div>
                </div>
              )}

              {/* Active call: dual-screen toggle between CallControls and Dialpad.
                  Default on connect (showDialpad=false) is CallControls.
                  Keypad button in CallControls → setShowDialpad(true) (DTMF view).
                  Back-to-Call button in the dialpad footer → setShowDialpad(false). */}
              {activeCall && !showDialpad && (
                <CallControls
                  activeCall={activeCall}
                  callConnected={callConnected}
                  callStatus={callStatus}
                  onHangup={handleHangup}
                  onMuteToggle={handleMuteToggle}
                  isMuted={isMuted}
                  isSpeaker={isSpeaker}
                  onSpeakerToggle={handleSpeakerToggle}
                  isBluetooth={isBluetooth}
                  onDeviceCycle={handleDeviceCycle}
                  onHold={handleHold}
                  isOnHold={isOnHold}
                  isHoldPending={isHoldPending}
                  onKeypad={handleShowDialpad}
                  onAdd={handleAddClick}
                  onTransfer={handleTransferClick}
                  isBridging={isBridgingState}
                  isTranslating={isTranslating}
                  onTranslate={() => setShowTranslate(prev => !prev)}
                  recordingSupported={recordingSupported}
                  isRecording={isRecording}
                  recordingBusy={recordingBusy}
                  recordingSeconds={recordingSeconds}
                  recordingError={recordingError}
                  onRecord={() => handleRecordToggle(true)}
                  onStop={() => handleRecordToggle(false)}
                />
              )}

              {/* Live Translate overlay */}
              {activeCall && showTranslate && (
                <div className="translate-modal-overlay" onClick={() => setShowTranslate(false)}>
                  <div className="translate-modal-sheet" onClick={(e) => e.stopPropagation()}>
                    <TranslateControls
                      callSid={activeCall?.parameters?.CallSid || activeCall?.sid || activeCall?.callSid || ''}
                      authToken={authToken}
                      selectedNumber={selectedNumber}
                      remoteNumber={number}
                      savedContacts={savedContacts}
                      isTranslating={isTranslating}
                      onTranslateChange={(active, usedLang) => {
                        setIsTranslating(active);
                        if (active && usedLang && number) {
                          lastTranslateRef.current = { number, lang: usedLang };
                        }
                        if (!active) setShowTranslate(false);
                      }}
                      onClose={() => setShowTranslate(false)}
                    />
                  </div>
                </div>
              )}

              {recallPrompt && !activeCall && (
                <TranslateRecallPrompt
                  recall={recallPrompt}
                  authToken={authToken}
                  selectedNumber={selectedNumber}
                  onReload={reloadSavedContacts}
                  onClose={() => setRecallPrompt(null)}
                />
              )}

              {/* ParticipantRoster intentionally hidden from the UI — the
                  underlying participants state is still populated (and
                  drives the handoff-grace early-release detector and other
                  internal flows) but we don't render the "Participants /
                  Unknown" block to the user. Re-enable here when there's a
                  clearer use case (e.g. visible 3-way roster with proper
                  contact-name resolution). */}

              {activeCall && showDialpad && (
                <>
                  <div className="active-call-footer active-call-footer-top" data-testid="active-call-footer-top">
                    <button
                      type="button"
                      className="back-to-call btn-primary"
                      onClick={handleHideDialpad}
                      data-testid="back-to-call-btn"
                      title="Return to call controls"
                    >
                      ← Back to Call
                    </button>
                    <button
                      type="button"
                      className="footer-hangup ctrl-btn hangup"
                      onClick={handleHangup}
                      data-testid="footer-hangup-btn-top"
                      title="Hang Up"
                    >
                      📞 End
                    </button>
                  </div>
                  <div className="number-display truva-glass-secondary">
                    <input
                      type="tel"
                      value={dialpadTarget ? dialpadNumber : dtmfEcho}
                      onChange={dialpadTarget
                        ? (e) => setDialpadNumber(e.target.value)
                        : undefined}
                      readOnly={!dialpadTarget}
                      onKeyDown={(e) => {
                        if (dialpadTarget) return;
                        if (/^[0-9*#]$/.test(e.key)) {
                          e.preventDefault();
                          handleDigit(e.key);
                        }
                      }}
                      placeholder={
                        dialpadTarget === 'add'
                          ? 'Number to add'
                          : dialpadTarget === 'transfer'
                          ? 'Transfer to'
                          : 'Press keypad digits'
                      }
                      data-testid="dialpad-entry-input"
                    />
                  </div>
                  <Dialpad
                    onDigit={dialpadTarget
                      ? (d) => setDialpadNumber((prev) => prev + d)
                      : handleDigit}
                    onCall={dialpadTarget ? handleDialpadEntrySubmit : undefined}
                    callDisabled={dialpadTarget ? !dialpadNumber.trim() : true}
                    hideCallButton={!dialpadTarget}
                    onBackspace={dialpadTarget
                      ? () => setDialpadNumber((prev) => prev.slice(0, -1))
                      : () => setDtmfEcho((prev) => prev.slice(0, -1))}
                  />
                </>
              )}

              {!activeCall && (
                <Dialpad
                  onDigit={handleDigit}
                  onCall={handleCall}
                  callDisabled={!String(number || '').trim() || !deviceReady}
                  onBackspace={handleBackspace}
                />
              )}
            </div>
          </div>
          </ErrorBoundary>
        )}

        {/* ─── Messages tab ─── */}
        {tab === 'messages' && (
          <ErrorBoundary inline label="Messages">
          <div className="main-view-wrapper">
            <Messages key={tabResetKeys.messages} selectedNumber={selectedNumber} authToken={authToken} composeTo={composeTo} setComposeTo={setComposeTo} setUnreadCount={setUnreadMessages} setPrefillContactNumber={setPrefillContactNumber} setTab={setTab} handleCall={handleCall} setNumber={setDialNumber} />
          </div>
          </ErrorBoundary>
        )}
        {/* ─── Contacts tab ─── */}
        {tab === 'contacts' && (
          <ErrorBoundary inline label="Contacts">
          <div className="main-view-wrapper">
            <Contacts key={tabResetKeys.contacts} selectedNumber={selectedNumber} authToken={authToken} setNumber={setDialNumber} setTab={setTab} handleCall={handleCall} setComposeTo={setComposeTo} prefillContactNumber={prefillContactNumber} setPrefillContactNumber={setPrefillContactNumber} />
          </div>
          </ErrorBoundary>
        )}

        {/* ─── Voicemail tab ─── */}
        {tab === 'voicemail' && (
          <ErrorBoundary inline label="Voicemail">
          <div className="main-view-wrapper">
            <Voicemail key={tabResetKeys.voicemail} authToken={authToken} onUnheardCount={setUnheardVoicemails} />
          </div>
          </ErrorBoundary>
        )}

        {/* ─── Settings tab ─── */}
        {tab === 'settings' && (
          <ErrorBoundary inline label="Settings">
          <div className="settings-shell">
            <Settings
              authToken={authToken}
              phoneCount={phoneNumbers.length}
              selectedNumber={selectedNumber}
              user={user}
              narrowLayout={narrowLayout}
              inPersonTranslateActive={liveTranslateActive}
              onToggleInPersonTranslate={handleTranslateToggleClick}
              onAccountDeleted={handleLogout}
              notificationPermission={notificationPermission}
            />
          </div>
          </ErrorBoundary>
        )}

        {/* ─── Recents tab ─── */}
        {tab === 'recents' && (
          <ErrorBoundary inline label="Recents">
          <div className="main-view-wrapper">
            <CallLog
                          key={tabResetKeys.recents}
                          authToken={authToken}
                          handleCall={handleCall}
                          setTab={setTab}
                          selectedNumber={selectedNumber}
                          entitlements={user && user.entitlements}
                          setPrefillContactNumber={setPrefillContactNumber}
                          setComposeTo={setComposeTo}
                        />
          </div>
          </ErrorBoundary>
        )}
      </main>

      {/* Always-visible chrome footer — short deploy SHA on every tab. */}
      <BuildStamp />

      {/* Bottom tab bar (only bottom instance) */}
      <nav className="tab-bar">
        {(narrowLayout ? NARROW_PRIMARY_TABS : TABS).map((t) => (
          <button
            key={t}
            type="button"
            className={`tab-btn ${tab === t ? 'active' : ''}`}
            onClick={() => {
              setMoreTabsOpen(false);
              setAccountMenuOpen(false);
              // During an active call the phone tab shows CallControls, not
              // the dialpad. Tapping bottom-nav "Keypad" should open the
              // in-call DTMF pad instead of doing nothing when already on
              // the phone tab.
              if (t === 'phone' && activeCall) {
                setTab('phone');
                setDtmfEcho('');
                setShowDialpad(true);
                return;
              }
              setTab(t);
                            if (t === 'messages' || t === 'recents' || t === 'contacts' || t === 'voicemail') {
                              // Tapping an already-active list tab returns to its root list view:
                              // bump that tab's reset key to remount the component fresh (clears
                              // any drill-down detail/compose state). Same behavior across all four.
                              setComposeTo(null);
                              setTabResetKeys((k) => ({ ...k, [t]: (k[t] || 0) + 1 }));
                            }
                            if (t === 'phone') {
                              // Resume/create AudioContext inside user gesture
                              ensureAudioStarted().catch(() => {});
                              setPhoneTabSelectedByUser(true);
                              // Try to register AudioContext with Twilio Device (one-time attempt)
                              try {
                                const audioCtx = window.__truvaAudioContext || (window.__truvaAudioContext = new (window.AudioContext || window.webkitAudioContext)());
                                if (deviceRef.current && deviceRef.current.audio && typeof deviceRef.current.audio.registerContext === 'function') {
                                  deviceRef.current.audio.registerContext(audioCtx);
                                  console.log('[AUDIO] device.audio.registerContext invoked (Phone tab click)');
                                }
                              } catch (e) {
                                console.warn('[AUDIO] Phone tab click audio registration/resume failed', e);
                              }
                            }
            }}
          >
            <span className="tab-icon">{TAB_ICONS[t]({ filled: tab === t })}</span>
            <span className="tab-label">{TAB_LABELS[t]}</span>
            {t === 'voicemail' && unheardVoicemails > 0 && (
              <span className="tab-badge">{unheardVoicemails > 99 ? '99+' : unheardVoicemails}</span>
            )}
            {t === 'messages' && unreadMessages > 0 && (
              <span className="tab-badge">{unreadMessages > 99 ? '99+' : unreadMessages}</span>
            )}
          </button>
        ))}
        {narrowLayout && (
          <div className="tab-more" ref={moreTabsRef}>
            <button
              type="button"
              className={`tab-btn tab-more-btn${moreTabsOpen || NARROW_MORE_TABS.includes(tab) ? ' active' : ''}`}
              aria-haspopup="menu"
              aria-expanded={moreTabsOpen}
              aria-label="More tabs"
              onClick={() => {
                setAccountMenuOpen(false);
                setMoreTabsOpen((o) => !o);
              }}
            >
              {/* Drawn at the same 20px as the other tab icons, not the U+22EF glyph: it is missing
                  from some iPad fonts and rendered as a boxed "?" that sat out of line. */}
              <span className="tab-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" style={{display:'block'}}>
                  <circle cx="3.5" cy="10" r="1.8" fill="currentColor" />
                  <circle cx="10" cy="10" r="1.8" fill="currentColor" />
                  <circle cx="16.5" cy="10" r="1.8" fill="currentColor" />
                </svg>
              </span>
              <span className="tab-label">More</span>
              {(unheardVoicemails > 0 && NARROW_MORE_TABS.includes('voicemail') && tab !== 'voicemail') && (
                <span className="tab-badge">{unheardVoicemails > 99 ? '99+' : unheardVoicemails}</span>
              )}
            </button>
            {moreTabsOpen && (
              <div className="overflow-menu overflow-menu--up" role="menu">
                {NARROW_MORE_TABS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="menuitem"
                    className={`overflow-menu-item${tab === t ? ' active' : ''}`}
                    onClick={() => {
                      setMoreTabsOpen(false);
                      setTab(t);
                    }}
                  >
                    <span className="overflow-menu-icon" aria-hidden="true">{TAB_ICONS[t]({ filled: tab === t })}</span>
                    <span>{TAB_LABELS[t]}</span>
                    {t === 'voicemail' && unheardVoicemails > 0 && (
                      <span className="overflow-menu-badge">{unheardVoicemails > 99 ? '99+' : unheardVoicemails}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>
    </div>
    </AuthProvider>
  );
}

export default App;
